/**
 * A fleet event in one sentence, for the Activity and workbench views: the raw payload stays out of
 * sight (in a tooltip). The event kinds are the workbench's Control API events, an open set, so
 * the known ones get their own words and the rest are read from the fields most events carry.
 */

import { monthYear } from '@aioe/shared/wording/dateTime';
import { ASSURANCE_WORDS, CLASSIFICATION_WORDS, ROUTINE_EVENT_REFUSAL_WORDS, ROUTINE_EVENT_SERVICES, exceptionDurationWords, scopeRequestWords, type OutsideEventSource, type RoutineEventRefusal, wrapUpAskWords, type AssuranceRating, type Classification, type PlanWrapUpAskInput } from '@aioe/shared';
import { orderSentence } from './nodeOrders.ts';

type Payload = Record<string, unknown>;

const str = (p: Payload, ...keys: string[]): string | undefined => {
  for (const k of keys) {
    const v = p[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return undefined;
};

const STATUS_TEXT: Record<string, string> = {
  thinking: 'is thinking',
  tool_calling: 'is using a tool',
  idle: 'is idle',
  waiting_approval: 'is waiting for approval',
  error: 'hit an error',
  stopped: 'stopped',
  done: 'finished',
};

/** "agent.status" → "Agent status". */
export const kindText = (kind: string) => kind.replace(/[._-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/^./, (c) => c.toUpperCase());

export function eventSentence(kind: string, payload: unknown): string {
  const p: Payload = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Payload) : {};
  const who = str(p, 'agentName', 'name', 'memberName') ?? 'An agent';
  const team = str(p, 'teamName', 'team');
  const inTeam = team ? ` in ${team}` : '';
  const status = str(p, 'status', 'state');
  switch (kind) {
    case 'agent.status':
    case 'session.status':
      return `${who}${inTeam} ${status ? (STATUS_TEXT[status] ?? status.replace(/_/g, ' ')) : 'changed state'}${str(p, 'currentTool', 'tool') ? `: ${str(p, 'currentTool', 'tool')}` : ''}.`;
    case 'team.started':
      return `${team ?? str(p, 'name') ?? 'A team'} started${str(p, 'agents') ? ` with ${str(p, 'agents')} agents` : ''}.`;
    case 'team.stopped':
      return `${team ?? str(p, 'name') ?? 'A team'} stopped.`;
    case 'permission.requested':
    case 'permission.request':
      return `${who}${inTeam} asks to ${str(p, 'title', 'kind') ?? 'use a tool'}.`;
    case 'permission.resolved':
      return `${who}'s request was ${str(p, 'decision') === 'deny' ? 'denied' : 'allowed'}.`;
  }
  // An audit row carried as an event (its action as the kind, its fields as the payload) reads as the Audit page says it.
  const audited = auditSentence({ action: kind, outcome: str(p, 'outcome'), target: str(p, 'target'), error: str(p, 'error'), detail: p.detail && typeof p.detail === 'object' && !Array.isArray(p.detail) ? (p.detail as Payload) : p });
  if (audited) return audited;
  const detail = str(p, 'summary', 'text', 'title', 'message', 'status');
  const lead = kindText(kind);
  if (!detail) return `${lead}${team ? `, ${team}` : ''}${str(p, 'agentName') ? `, ${str(p, 'agentName')}` : ''}.`;
  const short = detail.length > 160 ? `${detail.slice(0, 157)}…` : detail;
  return `${lead}: ${short}`;
}

const RUNTIME_NAMES: Record<string, string> = { 'claude-code': 'Claude Code', claude: 'Claude Code', codex: 'Codex', 'copilot-cli': 'Copilot', copilot: 'Copilot', gemini: 'Gemini', cursor: 'Cursor' };
const runtimeName = (id: string | undefined) => (id ? (RUNTIME_NAMES[id] ?? id) : undefined);
const PROVIDER_NAMES: Record<string, string> = { anthropic: 'Anthropic', openai: 'OpenAI', google: 'Google', gemini: 'Gemini', azure: 'Azure OpenAI', 'azure-openai': 'Azure OpenAI', cursor: 'Cursor', github: 'GitHub', 'github-copilot': 'GitHub Copilot', mistral: 'Mistral', openrouter: 'OpenRouter' };

/** Where the email a Cursor cloud agent ran as came from (`emailFrom`), in words. */
const EMAIL_FROM: Record<string, string> = {
  organisation: 'the email the organisation verified for this person',
  person: 'an email the person gave, which the organisation’s policy allows',
};

/** Where the audit trail is forwarded (`audit.forwarding.*` detail.kind), by name. */
const FORWARD_KINDS: Record<string, string> = { sentinel: 'Microsoft Sentinel', 'splunk-hec': 'Splunk', webhook: 'a web address' };
const forwardName = (k: string | undefined) => (k ? (FORWARD_KINDS[k] ?? k) : 'its destination');

/** How a membership changed (`organisation.member.*` detail.via), in words. */
const MEMBER_VIA: Record<string, string> = { invitation: 'by invitation' };
const memberVia = (d: Payload) => {
  const via = str(d, 'via');
  if (via) return MEMBER_VIA[via] ? ` ${MEMBER_VIA[via]}` : '';
  return str(d, 'invitation') ? ` ${MEMBER_VIA.invitation}` : '';
};

/** A desktop sign-in's step-up action (`step-up.*` detail.action), in words. */
const STEP_UP_ACTIONS: Record<string, string> = { 'node.roots.add': 'open another folder to agents', 'hosts.attach': 'attach to another machine', 'computer.control': 'control a computer' };

/** What an audit row needs for a sentence: the workbench's audit entry (platform.ts `auditEntrySchema`). */
export interface AuditLike {
  action: string;
  outcome?: string;
  target?: string;
  error?: string;
  detail?: Record<string, unknown>;
}

/** " after reading outside content (a web page)" for a tool call made in a session carrying the outside mark; "" otherwise. */
function afterOutside(d: Payload): string {
  if (d.outside !== true) return '';
  const sources = Array.isArray(d.outsideSources) ? d.outsideSources.filter((s): s is string => typeof s === 'string' && s.trim() !== '') : [];
  // The newest source, its own bracket ("a web page (example.com)") folded in so brackets never nest.
  const last = sources.at(-1)?.replace(/\s*\(([^()]*)\)\s*$/, ', $1');
  return ` after reading outside content${last ? ` (${last})` : ''}`;
}

/** Where a remote action came from (`detail.via`, D282), in words: "from the phone", "from the web". */
const REMOTE_VIA_WORDS: Record<string, string> = { phone: ' from the phone', web: ' from the web' };
const looksLikeId = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-|^[A-Za-z0-9_-]{20,}$/.test(v);

/**
 * Remote control of a live session (D282): the `remote.*` records, the platform's and the workbench's. The
 * detail carries `via`, the session or conversation (by title when it has one; never shown as an id) and the
 * run. "JP sent a message to Release notes from the phone"; without a name, "A message was sent to … from the web".
 */
/** A node's remote-management row: the node writes the sentence itself; the console adds who and from where. */
function nodeRemoteSentence(action: string, d: Payload, denied: boolean, error?: string): string {
  const said = str(d, 'sentence', 'message');
  const who = str(d, 'by', 'person') ?? 'Someone';
  const from = str(d, 'via') === 'phone' ? ' from the phone' : str(d, 'via') === 'web' ? ' from the console' : str(d, 'via') === 'order' ? ' by an order' : '';
  const what: Record<string, string> = {
    'node.remote.watch': 'watched the node',
    'node.remote.approve': 'approved a request on the node',
    'node.remote.deny': 'refused a request on the node',
    'node.remote.stop-member': 'stopped a team member on the node',
    'node.remote.restart': 'restarted the node’s service',
    'node.remote.update-check': 'checked the node for updates',
    'node.remote.update-install': 'installed an update on the node',
    'node.remote.service': 'changed whether the node starts at login',
    'node.remote.update': 'changed the node’s updates',
    'node.remote.routine': 'changed a routine on the node',
    'node.remote.sessions': 'changed remote control of the node’s sessions',
    'node.remote.setting': 'changed a node setting',
  };
  const base = said ?? `${who} ${what[action] ?? 'managed the node'}${from}`;
  if (denied) return `${base.replace(/[.]$/, '')}: refused${error ? ` (${error})` : str(d, 'code') ? ` (${str(d, 'code')})` : ''}.`;
  return /[.]$/.test(base) ? base : `${base}.`;
}

function remoteSentence(action: string, d: Payload, denied: boolean, error?: string): string {
  const named = str(d, 'personName', 'byName', 'name', 'actorName', 'person');
  // The platform's own row names the person as its actor, not in the detail: then the sentence is about the session.
  const who = named && !looksLikeId(named) ? named : undefined;
  const sub = (k: string): Payload => (d[k] && typeof d[k] === 'object' && !Array.isArray(d[k]) ? (d[k] as Payload) : {});
  const titled = str(d, 'title', 'sessionTitle', 'conversationTitle') ?? str(sub('session'), 'title') ?? str(sub('conversation'), 'title');
  const what = titled ?? (d.conversation || d.conversationId ? 'a conversation' : 'a session');
  const cap = (t: string) => t[0]!.toUpperCase() + t.slice(1);
  const from = REMOTE_VIA_WORDS[str(d, 'via') ?? ''] ?? '';
  const why = error ?? str(d, 'error', 'reason');
  // The workbench's own sentence, carried on after a colon: "…was refused: an owner must approve this."
  const because = why ? `: ${/^[A-Z][a-z]/.test(why) ? why[0]!.toLowerCase() + why.slice(1) : why}`.replace(/[.]$/, '') : '';
  const tool = str(d, 'tool', 'request', 'permissionTitle');
  const request = tool ? `${tool} in ${what}` : `a request in ${what}`;
  const by = who ? ` by ${who}` : '';
  switch (action) {
    case 'remote.send':
      if (denied) return `A message${who ? ` from ${who}` : ''} to ${what}${from} was refused${because}.`;
      return who ? `${who} sent a message to ${what}${from}.` : `A message was sent to ${what}${from}.`;
    case 'remote.interrupt':
      if (denied) return `Interrupting ${what}${from} was refused${because}.`;
      return who ? `${who} interrupted ${what}${from}.` : `${cap(what)} was interrupted${from}.`;
    case 'remote.stop':
      if (denied) return `Stopping ${what}${from} was refused${because}.`;
      return who ? `${who} stopped ${what}${from}.` : `${cap(what)} was stopped${from}.`;
    case 'remote.approve':
      if (denied) return `Approving ${request}${from}${by} was refused${because}.`;
      return who ? `${who} approved ${request}${from}.` : `${cap(request)} was approved${from}.`;
    case 'remote.deny':
      if (denied) return `Refusing ${request}${from}${by} was not accepted${because}.`;
      return who ? `${who} refused ${request}${from}.` : `${cap(request)} was refused${from}.`;
    case 'remote.session.on':
      if (denied) return `Opening ${what} to the web and the phone was refused${because}.`;
      return who ? `${who} opened ${what} to the web and the phone.` : `${cap(what)} was opened to the web and the phone.`;
    case 'remote.session.off':
      if (denied) return `Turning remote control off for ${what} was refused${because}.`;
      return who ? `${who} turned remote control off for ${what}${from}.` : `Remote control was turned off for ${what}${from}.`;
    case 'remote.board.pause':
      if (denied) return `Pausing the board of ${what}${from} was refused${because}.`;
      return who ? `${who} paused the board of ${what}${from}.` : `The board of ${what} was paused${from}.`;
    case 'remote.board.resume':
      if (denied) return `Resuming the board of ${what}${from} was refused${because}.`;
      return who ? `${who} resumed the board of ${what}${from}.` : `The board of ${what} was resumed${from}.`;
    case 'remote.handoff': {
      // D414: another account of the same runtime, or another runtime.
      const name = str(d, 'to');
      const to = name ? (str(d, 'toKind') === 'account' ? ` to the ${name} account` : ` to ${name}`) : '';
      if (denied) return `Handing ${what} off${to}${from} was refused${because}.`;
      return who ? `${who} handed ${what} off${to}${from}.` : `${cap(what)} was handed off${to}${from}.`;
    }
    case 'remote.press':
    case 'ui.press': {
      // D414: a press on a block an agent showed: a form sent, or a button pressed. `ui.press` is the workbench's own row,
      // which counts the values it was sent (`values`), never holding them.
      const form = d.form === true || (typeof d.values === 'number' && d.values > 0);
      const thing = form ? 'a form' : 'a button';
      if (denied) return `Answering ${thing} in ${what}${from} was refused${because}.`;
      return form ? (who ? `${who} sent a form in ${what}${from}.` : `A form was sent in ${what}${from}.`) : who ? `${who} pressed a button in ${what}${from}.` : `A button was pressed in ${what}${from}.`;
    }
    case 'remote.session.end-all':
      if (denied) return `Ending every remote control at once was refused${because}.`;
      return who ? `${who} ended every remote control at once with the kill switch.` : 'Every remote control was ended at once with the kill switch.';
  }
  return `${cap(what)} was controlled remotely${from}.`;
}

/**
 * An audit row in one plain sentence, for the actions whose raw names say little to a person;
 * undefined for the rest (shown by their action name as before). Workbench actions are an open set.
 */
export function auditSentence(a: AuditLike): string | undefined {
  const d: Payload = a.detail ?? {};
  const denied = a.outcome === 'denied';
  const runtime = runtimeName(str(d, 'runtime'));
  const inTerminal = runtime ? `in the ${runtime} terminal` : 'in a terminal';
  switch (a.action) {
    case 'terminal.mcp.refused': {
      const server = str(d, 'server') ?? a.target;
      return `${server ? `The repository’s tool server “${server}”` : 'A repository’s tool server'} was not started ${inTerminal}: it used the name of the workbench’s own server.`;
    }
    case 'terminal.tool.denied': {
      const tool = str(d, 'tool') ?? 'A tool';
      const by = str(d, 'by') ?? '';
      const after = afterOutside(d);
      if (by === 'reserved-name') return `${tool} was refused ${inTerminal}: its server uses the name of the workbench’s own server, which the workbench did not give this terminal.`;
      if (d.cannotAsk === true) return `${tool} was refused ${inTerminal}${after}: the organisation’s policy asks first, and ${runtime ?? 'this tool'} cannot ask.`;
      if (by.startsWith('gate:')) return `${tool} was refused ${inTerminal}${after} by the organisation’s tool rules.`;
      return `${tool} was refused ${inTerminal}${after} by the organisation’s policy.`;
    }
    case 'tool.floor':
      return `A command was refused by the floor${str(d, 'words') ? `: ${str(d, 'words')}` : str(d, 'rule') ? ` (${str(d, 'rule')})` : ''}${str(d, 'by') === 'denyCommands' ? ", one of your organisation's own patterns" : ''}.`;
    case 'tool.call': {
      // The workbench's tool gate (shared/schemas/tools.ts): one call, what the rules said and who answered.
      const tool = str(d, 'tool') ?? afterColon(a.target) ?? 'A tool';
      const on = str(d, 'server') ? ` on ${str(d, 'server')}` : '';
      const after = afterOutside(d);
      const answer = str(d, 'answer');
      if (denied) return answer ? `A person refused ${tool}${on}${after}.` : `${tool}${on} was refused${after} by the organisation’s tool rules.`;
      if (answer?.startsWith('allowed')) return `A person allowed ${tool}${on}${answer === 'allowed for this task' ? ' for the task' : ''}${after}.`;
      const would = str(d, 'would');
      if (would) return `${tool}${on} ran${after}; enforcing the tool rules would have ${would === 'off' ? 'refused it' : 'asked a person first'}.`;
      if (a.outcome === 'error') return `${tool}${on} failed${after}.`;
      return `${tool}${on} ran${after}.`;
    }
    case 'queue.claim': {
      const task = [str(d, 'task'), str(d, 'title')].filter(Boolean).join(' ') || 'A task';
      if (d.kind === 'person') return denied ? `${task} is ${str(d, 'person') ?? 'a person'}’s, and was not handed to an agent without their say.` : `${str(d, 'person') ?? 'A person'} started on ${task}${str(d, 'deviceName') ? ` from ${str(d, 'deviceName')}` : ''}.`;
      if (denied && str(d, 'error') === 'persons_task') return `${task} is ${str(d, 'person') ?? 'a person'}’s, and a claim of it by another session was refused.`;
      return `${str(d, 'deviceName') ?? 'A machine'} picked up ${task} from the queue.`;
    }
    case 'queue.release': {
      const task = [str(d, 'task'), str(d, 'title')].filter(Boolean).join(' ') || 'A task';
      const by = str(d, 'deviceName');
      if (str(d, 'reason') === 'revoked') return `${task} was taken back from ${by ?? 'the machine holding it'}${d.requeued === false ? '' : ' and put back in the queue'}.`;
      return `${by ?? 'A machine'} let ${task} go: its session stopped${str(d, 'why') ? ` (${str(d, 'why')})` : ''}.`;
    }
    case 'queue.takeover': {
      const task = [str(d, 'task'), str(d, 'title')].filter(Boolean).join(' ') || 'A task';
      const sub = (k: string): Payload => (d[k] && typeof d[k] === 'object' && !Array.isArray(d[k]) ? (d[k] as Payload) : {});
      const from = str(sub('from'), 'deviceName');
      const to = str(sub('to'), 'deviceName');
      const by = str(d, 'byName');
      const how = d.forced === true ? ' although its lease was still fresh' : d.stale === true ? ' once its lease had run out' : '';
      const where = to ? `sent to ${to}` : d.requeued === false ? 'left as it was' : 'put back in the queue';
      return `${by ? `${by} took` : 'Someone took'} ${task} over${from ? ` from ${from}` : ''}${how}, and it was ${where}${str(d, 'reason') ? `: ${str(d, 'reason')}` : '.'}`;
    }
    case 'queue.lapse': {
      const task = [str(d, 'task'), str(d, 'title')].filter(Boolean).join(' ') || 'A task';
      return d.unacknowledgedDispatch === true ? `${task} went back to the queue: ${str(d, 'deviceName') ?? 'the machine it was sent to'} never answered.` : `${task} went back to the queue: ${str(d, 'deviceName') ?? 'the machine holding it'} had not been heard from for too long.`;
    }
    case 'plan.row.alongside': {
      const row = str(d, 'row') ?? 'a roadmap row';
      const sub = (k: string): Payload => (d[k] && typeof d[k] === 'object' && !Array.isArray(d[k]) ? (d[k] as Payload) : {});
      const other = str(sub('with'), 'teamName') ?? 'Another team';
      return `${other} and ${str(d, 'teamName') ?? 'another team'} are both on ${row}, agreed by ${str(d, 'by') ?? 'a person'}.`;
    }
    case 'plan.queue': {
      const plan = str(d, 'ref') ?? 'A plan';
      const after = d.queuedAfter && typeof d.queuedAfter === 'object' ? (d.queuedAfter as Payload) : {};
      return `${plan} was queued after ${str(after, 'planRef') ?? 'another plan'}: it waits as a draft and is approved when that plan finishes.`;
    }
    case 'plan.approve': {
      const plan = str(d, 'ref') ?? 'A plan';
      if (d.auto !== true) return `${plan} was approved${str(d, 'note') ? `: ${str(d, 'note')}` : '.'}`;
      const after = d.queuedAfter && typeof d.queuedAfter === 'object' ? (d.queuedAfter as Payload) : {};
      return `${plan} was approved: ${str(after, 'planRef') ?? 'the plan it waited for'} ${str(d, 'holder') === 'abandoned' ? 'was abandoned' : str(d, 'holder') === 'removed' ? 'is gone' : 'is finished'}.`;
    }
    case 'plan.abandon': {
      const plan = str(d, 'ref') ?? 'A plan';
      const n = Array.isArray(d.cancelled) ? d.cancelled.length : 0;
      return `${plan} was abandoned${n ? `, and ${n} ${n === 1 ? 'task' : 'tasks'} carrying it ${n === 1 ? 'was' : 'were'} cancelled` : ''}${str(d, 'reason') ? `: ${str(d, 'reason')}` : '.'}`;
    }
    case 'project.root.restore':
      return `${str(d, 'rootName') ?? 'A repository'} was added back to the project; its board is open for writes again.`
    case 'project.root.remove': {
      const name = str(d, 'rootName') ?? str(d, 'root') ?? 'A repository';
      const cancelled = typeof d.cancelled === 'number' ? d.cancelled : 0;
      const released = typeof d.released === 'number' ? d.released : 0;
      const work = [cancelled ? `${cancelled} ${cancelled === 1 ? 'task' : 'tasks'} cancelled` : '', released ? `${released} ${released === 1 ? 'claim' : 'claims'} released` : ''].filter(Boolean).join(', ');
      return `${name} was removed from the project${work ? ` (${work})` : ''}; its board stays read-only for 30 days.`;
    }
    case 'part.place': {
      const part = str(d, 'title') ?? 'A part';
      const where = str(d, 'rootName') ?? 'its repository';
      if (denied) return `${part} could not be placed on a machine with ${where}: ${str(d, 'error') ?? 'the machine refused it'}.`;
      return d.state === 'waiting_for_device' ? `${part} is waiting for a machine with ${where} to come online.` : `${part} was placed on a machine with ${where}${d.afterWait === true ? ' once one came online' : ''}.`;
    }
    case 'board.write':
      if (denied && a.error === 'not_holder') return `A late write to ${str(d, 'task') ?? a.target ?? 'a task'} was refused: another machine holds it now.`;
      return denied ? 'A write to a board task was refused.' : 'A board task was written.';
    case 'secret.open':
      if (denied && a.error === 'wrong_owner') return `${str(d, 'name') ?? 'A secret'} was not opened on this node: it is sealed for another person, and a person’s secret opens only for their own work.`;
      return undefined;
    case 'secret.use':
      if (denied && str(d, 'reason') === 'no-key-for-person') {
        const id = str(d, 'provider');
        const provider = id ? (PROVIDER_NAMES[id] ?? id) : undefined;
        return `${str(d, 'agent') ?? 'An agent'} was not started: there is no ${provider ? `${provider} key` : 'key'} for the person it works for on this machine, and another person’s key is never used instead.`;
      }
      return undefined;
    case 'cloud.cursor.token':
    case 'cloud.cursor.start':
    case 'cloud.cursor.follow-up': {
      const email = str(d, 'email');
      const as = email ? ` as ${email}` : '';
      const from = EMAIL_FROM[str(d, 'emailFrom') ?? ''];
      const source = from ? `, ${from}` : '';
      if (a.action === 'cloud.cursor.token') return `The organisation’s Cursor key was used to sign in${as}${source}.`;
      if (a.action === 'cloud.cursor.start') return `A Cursor cloud agent was started with the organisation’s key${as}${source}.`;
      return `A follow-up went to a Cursor cloud agent with the organisation’s key${as}${source}.`;
    }
  }
  return platformSentence(a, d);
}

const classWord = (v: string | undefined) => (v && v in CLASSIFICATION_WORDS ? CLASSIFICATION_WORDS[v as Classification] : 'unclassified');
const assuranceWord = (v: string | undefined) => (v && v in ASSURANCE_WORDS ? ASSURANCE_WORDS[v as AssuranceRating] : 'a new rating');
const quoted = (t: string | undefined) => (t ? ` “${t}”` : '');

/** The platform's own actions (audit forwarding, operator access, single sign-on, provisioning, membership, sign-in, status), in plain words. */
function platformSentence(a: AuditLike, d: Payload): string | undefined {
  const target = a.target;
  const failed = a.outcome === 'error';
  const denied = a.outcome === 'denied';
  switch (a.action) {
    // Audit export and forwarding.
    case 'audit.export': {
      const format = str(d, 'format');
      const as = format === 'csv' ? ' as a spreadsheet (CSV)' : format === 'sentinel' ? ' in Microsoft Sentinel’s format' : format ? ` as ${format.toUpperCase()}` : '';
      return `The audit trail was exported${as}.`;
    }
    case 'audit.settings': {
      const to = (d.to && typeof d.to === 'object' ? d.to : {}) as Record<string, unknown>;
      const ip = { full: 'the whole address', shortened: 'the address shortened to its network', off: 'no address' }[String(to.ip)] ?? 'the address as before';
      const place = { city: 'the town or city', region: 'the state or region', country: 'the country only', off: 'no place' }[String(to.location)] ?? 'the place as before';
      const ua = { full: 'the app or browser in full', summary: 'a summary of the app or browser', off: 'nothing of the browser' }[String(to.userAgent)] ?? 'the app or browser as before';
      const days = typeof to.keepDays === 'number' ? (to.keepDays === 0 ? ', for as long as each entry' : `, for ${to.keepDays} ${to.keepDays === 1 ? 'day' : 'days'}`) : '';
      return `The audit trail now keeps ${ip}, ${place} and ${ua}${days}.`;
    }
    case 'runs.forwarding.set':
      return str(d, 'kind') === 'off' ? 'Run forwarding was turned off.' : `Runs are now forwarded to ${str(d, 'host') ?? 'an observability tool'} (${str(d, 'kind')}, ${str(d, 'content') ?? 'metadata'})${str(d, 'start') === 'beginning' ? ', from the beginning' : ''}.`;
    case 'runs.forwarding.test':
      return d.ok === true ? 'A test trace was sent to the run forwarding destination and accepted.' : `A test trace to the run forwarding destination was not accepted${num(d, 'status') ? ` (HTTP ${num(d, 'status')})` : ''}.`;
    case 'audit.forwarding.set': {
      const kind = str(d, 'kind') ?? target;
      if (kind === 'off') return 'Audit forwarding was turned off.';
      const host = str(d, 'host');
      return `The audit trail is now forwarded to ${forwardName(kind)}${host ? ` at ${host}` : ''}${d.secretChanged === true ? ', with a new secret' : ''}.`;
    }
    case 'audit.forwarding.test':
      return failed || d.ok === false ? `A test entry could not be sent to ${forwardName(str(d, 'kind') ?? target)}${str(d, 'status') ? ` (it answered ${str(d, 'status')})` : ''}.` : `A test entry was sent to ${forwardName(str(d, 'kind') ?? target)}.`;

    // Mesh webhooks.
    case 'mesh.webhook.created':
      return `A mesh webhook to ${str(d, 'host') ?? 'an address'} was added${Array.isArray(d.events) ? ` for ${d.events.length} ${d.events.length === 1 ? 'event' : 'events'}` : ''}.`;
    case 'mesh.webhook.updated':
      return d.enabled === false ? `The mesh webhook to ${str(d, 'host') ?? 'an address'} was turned off.` : d.enabled === true ? `The mesh webhook to ${str(d, 'host') ?? 'an address'} was turned back on.` : `The events of the mesh webhook to ${str(d, 'host') ?? 'an address'} were changed.`;
    case 'mesh.webhook.deleted':
      return `The mesh webhook to ${str(d, 'host') ?? 'an address'} was deleted.`;
    case 'mesh.webhook.tested':
      return failed || d.ok === false ? `A test event could not be sent to ${str(d, 'host') ?? 'the mesh webhook'}${str(d, 'status') ? ` (it answered ${str(d, 'status')})` : ''}.` : `A test event was sent to ${str(d, 'host') ?? 'the mesh webhook'}.`;
    case 'mesh.webhook.disabled':
      return `The mesh webhook to ${str(d, 'host') ?? 'an address'} was turned off after ${str(d, 'failures') ?? 'repeated'} failed deliveries in a row.`;

    // Break-glass operator access.
    case 'operator.access.open': {
      const who = str(d, 'operator') ?? 'A platform operator';
      const minutes = str(d, 'minutes');
      const org = str(d, 'organisation');
      return `${who} opened access to ${org ?? 'this organisation'}${minutes ? ` for ${minutes} minutes` : ''}${str(d, 'reason') ? `: “${str(d, 'reason')}”` : ''}.`;
    }
    case 'operator.access.close':
      return `${str(d, 'closedBy') ?? 'A platform operator'} closed ${str(d, 'opener') && str(d, 'opener') !== str(d, 'closedBy') ? `${str(d, 'opener')}’s` : 'their'} access to the organisation.`;
    case 'operator.access.read':
      return `A platform operator read ${target ?? 'the organisation’s records'} under open access${str(d, 'reason') ? `: “${str(d, 'reason')}”` : ''}.`;
    case 'relay.channel.open':
      return `A live ${str(d, 'method') === 'terminals.subscribe' ? 'terminal' : str(d, 'method') === 'sessions.follow' ? 'transcript' : 'channel'} to the machine was opened from the app${d.input === true ? ', with typing' : ''}.`;
    case 'relay.channel.close': {
      const what = str(d, 'method') === 'terminals.subscribe' ? 'terminal' : str(d, 'method') === 'sessions.follow' ? 'transcript' : 'channel';
      const secs = num(d, 'seconds');
      return `A live ${what} to the machine was closed${secs !== undefined ? ` after ${secs < 60 ? `${secs} s` : `${Math.round(secs / 60)} min`}` : ''}.`;
    }
    // A node's console (relay/shell.ts): who, how long and how much each way; never what was typed or shown.
    case 'relay.shell.open':
      return `${str(d, 'person') ?? 'Someone'} opened a node's console from the app${d.input === true ? ', to type in it' : ', to watch it'}.`;
    case 'relay.shell.close': {
      const secs = num(d, 'seconds');
      const typed = num(d, 'bytesIn');
      const shown = num(d, 'bytesOut');
      const size = (n: number) => (n < 1024 ? `${n} bytes` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);
      const after = secs !== undefined ? ` after ${secs < 60 ? `${secs} s` : `${Math.round(secs / 60)} min`}` : '';
      const amounts = typed !== undefined && shown !== undefined ? ` (${size(typed)} typed, ${size(shown)} shown)` : '';
      return `${str(d, 'person') ?? 'Someone'} closed a node's console${after}${amounts}.`;
    }
    case 'operator.access.export':
      return `The platform access record was exported as a spreadsheet (CSV)${num(d, 'rows') !== undefined ? `, ${num(d, 'rows')} rows` : ''}.`;

    // Membership.
    case 'organisation.member.join':
      return `A person joined the organisation${memberVia(d)}${str(d, 'role') ? ` as ${str(d, 'role')}` : ''}.`;
    case 'organisation.member.leave':
      return str(d, 'reason') === 'account deleted' ? 'A person left the organisation when they deleted their account.' : 'A person left the organisation.';
    case 'organisation.member.remove':
      return `A person was removed from the organisation${memberVia(d)}${d.signedOut === true ? ' and signed out' : ''}.`;
    case 'organisation.member.role': {
      const to = str(d, 'to');
      if (denied) return `A person’s role was not changed${to ? ` to ${to}` : ''}: the organisation would have no owner left.`;
      return `A person’s role changed${str(d, 'from') ? ` from ${str(d, 'from')}` : ''}${to ? ` to ${to}` : ''}${memberVia(d)}.`;
    }

    // Desktop sign-in and step-up.
    case 'device.sign-in':
      if (denied) return 'A desktop sign-in was refused: it was someone other than the person who set up that workbench.';
      return 'A person signed in on their workbench.';
    case 'device.sign-out':
      return 'A person signed out on their workbench.';
    case 'step-up.would-refuse':
    case 'step-up.refused': {
      const action = str(d, 'action');
      const doing = action ? (STEP_UP_ACTIONS[action] ?? action.replace(/[._-]+/g, ' ')) : 'do that';
      return a.action === 'step-up.refused'
        ? `A workbench was stopped from trying to ${doing}: it needs a recent desktop sign-in.`
        : `A workbench was allowed to ${doing} without a recent desktop sign-in; once the sign-in rule is enforced it would be stopped.`;
    }

    // Design systems.
    case 'catalogue.design-system.download':
      return `${str(d, 'name') ?? target ?? 'A design system'} was downloaded${str(d, 'device') ? ' to a workbench' : ''}.`;

    // The status page.
    case 'status.incident.open':
      return `A status incident was opened${str(d, 'title') ? `: “${str(d, 'title')}”` : ''}.`;
    case 'status.incident.update':
      return 'A status incident was updated.';
    case 'status.incident.resolve':
      return 'A status incident was resolved.';
    case 'status.incident.reopen':
      return 'A status incident was reopened.';
    case 'platform.alert.opened':
      return `A production alert opened a status incident${str(d, 'component') ? ` for ${str(d, 'component')}` : ''}.`;
    case 'platform.alert.resolved':
      return 'A production alert cleared and its status incident was resolved.';
  }
  return areaSentence(a, d);
}

const num = (d: Payload, k: string): number | undefined => (typeof d[k] === 'number' ? (d[k] as number) : undefined);
const len = (d: Payload, k: string): number | undefined => (Array.isArray(d[k]) ? (d[k] as unknown[]).length : undefined);
const many = (n: number | undefined, one: string, more = `${one}s`) => (n === undefined ? undefined : `${n} ${n === 1 ? one : more}`);
const usd = (v: number | undefined) => (v === undefined ? undefined : `US$${v.toFixed(2)}`);
const plain = (t: string | undefined) => (t ? t.replace(/[._-]+/g, ' ') : undefined);
/** "2026-10" → "October 2026". */
const monthText = (m: string | undefined) => {
  const hit = m ? /^(\d{4})-(\d{2})/.exec(m) : null;
  return hit ? monthYear(Date.UTC(Number(hit[1]), Number(hit[2]) - 1, 1), { timeZone: 'UTC' }) : (m ?? 'a month');
};
const VENDORS: Record<string, string> = { anthropic: 'Anthropic', openai: 'OpenAI', google: 'Google', github: 'GitHub', cursor: 'Cursor', claude: 'Claude', codex: 'Codex', copilot: 'Copilot', gemini: 'Gemini' };
const vendorText = (v: string | undefined) => (v ? (VENDORS[v] ?? PROVIDER_NAMES[v] ?? v) : undefined);
/** The part of a "Person: Vendor" target after the colon (the name the platform wrote), else the whole target. */
const afterColon = (t: string | undefined) => (t && t.includes(': ') ? t.slice(t.indexOf(': ') + 2) : t);

/** Why a workbench was refused an administrator's action (auth/desktopRoles.ts), after a colon. */
const refusalWords = (code: string | undefined) =>
  code === 'key_bound_required' ? ': the workbench’s token is not bound to it' : code === 'stale_roles' ? ': the workbench has no recent sign-in' : code === 'forbidden' ? ': not an administrator' : '';

/** A secret’s name from its "scope: name" target. */
const secretName = (t: string | undefined) => (t ? `The secret “${afterColon(t)}”` : 'A secret');

/** The rest of the platform's audit actions, by area, in plain words (every action the API writes has a sentence; a check keeps it so). */
function areaSentence(a: AuditLike, d: Payload): string | undefined {
  const target = a.target;
  const failed = a.outcome === 'error';
  const denied = a.outcome === 'denied';
  const reason = str(d, 'reason');
  const why = reason ? `: ${reason}` : '.';
  switch (a.action) {
    // A project's catalogue of apps and routines (routes/projectCatalogue.ts): what people share, and its owners' review.
    case 'apps.publish':
    case 'apps.review':
    case 'apps.withdraw':
    case 'apps.install':
    case 'routines.publish':
    case 'routines.review':
    case 'routines.withdraw':
    case 'routines.install':
    case 'workflows.publish':
    case 'workflows.review':
    case 'workflows.withdraw':
    case 'workflows.install': {
      const noun = a.action.startsWith('apps.') ? 'app' : a.action.startsWith('routines.') ? 'routine' : 'workflow';
      const name = str(d, 'id');
      const version = str(d, 'version');
      const it = `The ${noun}${name ? ` “${name}”` : ''}${version ? ` ${version}` : ''}`;
      const verb = a.action.slice(a.action.indexOf('.') + 1);
      if (denied) return { publish: `A ${noun} could not be published${str(d, 'error') ? ` (${plain(str(d, 'error'))})` : ''}.`, review: `A review of a ${noun} was refused${str(d, 'error') ? ` (${plain(str(d, 'error'))})` : ''}.`, withdraw: `Withdrawing a ${noun} was refused: only its author or an owner may.`, install: `A ${noun} could not be added: it changed since it was listed.` }[verb];
      if (verb === 'publish') return `${it} was published${str(d, 'status') === 'pending' ? ', to wait for review' : ' and listed'}.`;
      if (verb === 'review') return str(d, 'decision') === 'reject' ? `${it} was not accepted${why}` : `${it} was approved and listed.`;
      if (verb === 'withdraw') return `${it} was withdrawn from the project's catalogue.`;
      return `${it} was added by a person${num(d, 'installs') ? ` (${many(num(d, 'installs'), 'person', 'people')} so far)` : ''}.`;
    }
    // Workflows (D401, workflows/service.ts): started here or by an event, the runs machines report, and their approvals.
    case 'workflows.start': {
      const what = (target ?? '').split('/').slice(1).join('/') || 'a workflow';
      if (denied) return `Starting the workflow ${what} was refused${str(d, 'error') ? ` (${plain(str(d, 'error'))})` : ''}.`;
      return str(d, 'trigger') === 'event' ? `An event your organisation forwarded started the workflow ${what} on ${many(num(d, 'orders') ?? 0, 'machine')}.` : `The workflow ${what} was started here on a machine.`;
    }
    case 'workflows.start.refused':
      return `A machine did not start a workflow${str(d, 'code') ? ` (${plain(str(d, 'code'))})` : ''}.`;
    case 'workflows.run':
      return `A machine reported a run of the workflow ${str(d, 'workflowId') ?? ''} ${str(d, 'version') ?? ''}`.trim() + '.';
    case 'workflows.approval.ask':
      return `A workflow's approval${str(d, 'stepId') ? ` (${str(d, 'stepId')})` : ''} is waiting for a person.`;
    case 'workflows.approval.decide':
      if (denied) return `A decision on a workflow's approval was refused${str(d, 'error') ? ` (${plain(str(d, 'error'))})` : ''}.`;
      return `A workflow's approval was ${str(d, 'decision') ?? 'decided'}${reason ? `: ${reason}` : '.'}`;
    case 'workflows.approval.decided':
      return `The machine recorded a workflow's approval as ${str(d, 'outcome') ?? 'decided'}${d.timedOut === true ? ', because no one decided in time' : ''}${reason ? `: ${reason}` : '.'}`;
    case 'apps.revoke':
      return `The organisation withdrew its approval of the app${str(d, 'id') ? ` “${str(d, 'id')}”` : ''}: it stops reading from and sending outside the Workbench at each machine's next policy pull.`;
    // Apps governance (D364), as the workbenches record it: consents, withdrawals, policy stops and data sent out.
    case 'microapp.consent': {
      const app = str(d, 'app');
      const it = `${app ? `“${app}”` : 'an app'}${str(d, 'version') ? ` ${str(d, 'version')}` : ''}`;
      const who = str(d, 'kind') === 'organisation' ? 'for the organisation' : str(d, 'kind') === 'team' ? 'for the team' : 'for themselves';
      const what = [num(d, 'external') ? `${many(num(d, 'external'), 'outside source')}` : '', d.sendsOut === true ? 'sends data out' : ''].filter(Boolean).join(', ');
      return `${str(d, 'by') ?? 'A person'} allowed what ${it} reads and sends, ${who}${what ? ` (${what})` : ''}.`;
    }
    case 'microapp.consent.withdrawn':
      return `The approval of what “${str(d, 'app') ?? 'an app'}” reads and sends was withdrawn on a machine${str(d, 'because') ? `: ${str(d, 'because')}` : '.'}`;
    case 'microapp.refused':
      return `The policy stopped part of “${str(d, 'app') ?? 'an app'}”${str(d, 'line') ? `: ${str(d, 'line')}` : a.error ? `: ${a.error}` : '.'}`;
    case 'microapp.egress':
      if (denied) return `“${str(d, 'app') ?? 'An app'}” was stopped from sending data${str(d, 'to') ? ` to ${str(d, 'to')}` : ''}: what it would send is labelled above what may leave the Workbench.`;
      return failed ? `“${str(d, 'app') ?? 'An app'}” could not send data${str(d, 'to') ? ` to ${str(d, 'to')}` : ''}${a.error ? `: ${a.error}` : '.'}` : `“${str(d, 'app') ?? 'An app'}” sent data out of the Workbench${str(d, 'to') ? ` to ${str(d, 'to')}` : ''}.`;
    // Agent to agent (A2A): agents of other systems calling in.
    case 'a2a.expose':
      return `${target ?? 'An agent'} was made available to agents outside the organisation.`;
    case 'a2a.unexpose':
      return `${target ?? 'An agent'} is no longer available to agents outside the organisation.`;
    case 'a2a.settings':
      return 'The settings for agents outside the organisation calling in were changed.';
    case 'a2a.message': {
      const caller = str(d, 'callerName') ?? 'An outside agent';
      return denied ? `A message from ${caller} to ${target ?? 'an agent'} was refused.` : `${caller} sent a message to ${target ?? 'an agent'}.`;
    }
    case 'a2a.reply':
      return denied ? `A reply on ${target ? `the task “${target}”` : 'a task'} was refused.` : `A reply was added to ${target ? `the task “${target}”` : 'a task'}.`;
    case 'a2a.cancel':
      return denied ? `Cancelling ${target ? `the task “${target}”` : 'a task'} was refused: the agent could not be reached.` : `${target ? `The task “${target}”` : 'A task'} was cancelled.`;

    // Microsoft Agent 365.
    case 'agent365.connect':
      return `${target ? `The agent blueprint “${target}”` : 'An agent blueprint'} was connected to Microsoft Agent 365.`;
    case 'agent365.disconnect':
      return `${target ? `The agent blueprint “${target}”` : 'An agent blueprint'} was disconnected from Microsoft Agent 365.`;
    case 'agent365.register':
      return `${target ?? 'An agent'} was registered with Microsoft Agent 365${str(d, 'problem') ? `, with a problem: ${str(d, 'problem')}` : ''}.`;
    case 'agent365.update':
      return `${target ?? 'An agent'}’s registration with Microsoft Agent 365 was updated${str(d, 'problem') ? `, with a problem: ${str(d, 'problem')}` : ''}.`;
    case 'agent365.retry.all': {
      const retried = num(d, 'retried');
      const bad = num(d, 'failed');
      return `Every team’s registration with Microsoft Agent 365 was tried again${retried !== undefined ? ` (${many(retried, 'team', 'teams')}${bad ? `, ${bad} still waiting` : ', all registered'})` : ''}.`;
    }
    case 'agent365.unregister':
      return d.identityKept === true ? `${target ?? 'An agent'} left its team: its Microsoft Agent 365 card was removed and its identity kept in Entra until an administrator removes it.` : `${target ?? 'An agent'} was removed from Microsoft Agent 365.`;
    case 'agent365.reconcile': {
      const orphans = num(d, 'orphans');
      return `The agent identities in Entra were checked${orphans !== undefined ? `: ${orphans ? `${many(orphans, 'identity', 'identities')} ${orphans === 1 ? 'belongs' : 'belong'} to no current member` : 'every one belongs to a current member'}` : ''}.`;
    }
    case 'agent365.identity.disable':
      return `${target ? `The agent identity “${target}”` : 'An agent identity'} was disabled in Entra: it belonged to no current member.`;
    case 'agent365.identity.delete':
      return `${target ? `The agent identity “${target}”` : 'An agent identity'} was deleted from Entra: it belonged to no current member and had been disabled.`;
    case 'agent365.token':
      return `A workbench was given a sign-in for the agent ${target ?? 'it works as'}.`;
    case 'agent365.order.disable':
      return `An order to turn off ${target ?? 'an agent'} was given${why}`;
    case 'agent365.order.quarantine':
      return `An order to quarantine ${target ?? 'an agent'} was given${why}`;
    case 'agent365.order.enable':
      return `An order to turn ${target ?? 'an agent'} back on was given${why}`;
    case 'agent365.order.cancel':
      return `The order for ${target ?? 'an agent'} was cancelled.`;

    // The audit trail itself.
    case 'audit.verify': {
      const entries = many(num(d, 'entries'), 'entry', 'entries');
      if (failed || d.ok === false) return `The audit trail was checked and a break was found${entries ? ` among ${entries}` : ''}.`;
      return `The audit trail was checked${entries ? `: ${entries}` : ''}, none altered.`;
    }

    // Backups of workbench settings and work.
    case 'backup.purge':
      return 'An expired backup was deleted.';
    case 'backup.recovery-key': {
      const holders = many(len(d, 'holders'), 'person', 'people');
      return `The organisation’s recovery key was ${d.replaced === true ? 'replaced' : 'set'}${holders ? `, held by ${holders}` : ''}.`;
    }
    case 'backup.recovery.refused':
      return `Organisation recovery from a workbench was refused${refusalWords(str(d, 'refused'))}.`;
    case 'backup.recovery.access':
      return 'A holder of the recovery key was given access to a person’s backups.';
    case 'backup.repository.create':
      return 'A backup store was created for a person.';
    case 'backup.recipient.add':
      return 'A key was added to a person’s backups, so one more holder can open them.';
    case 'backup.recipient.remove':
      return 'A key was removed from a person’s backups.';
    case 'backup.restore':
      return `A backup was restored${str(d, 'via') === 'console' ? ' from the console' : ' to a workbench'}.`;
    case 'backup.retention':
      return `${many(num(d, 'snapshots'), 'old backup') ?? 'Old backups'} ${num(d, 'snapshots') === 1 ? 'was' : 'were'} retired to stay within the ${str(d, 'plan') ?? 'plan'}’s limit.`;
    case 'backup.snapshot':
      return `A backup was taken${str(d, 'via') === 'console' ? ' from the console' : ' by a workbench'}${str(d, 'reason') ? ` (${str(d, 'reason')})` : ''}.`;
    case 'backup.snapshot.delete':
      return 'A backup was deleted by its owner.';
    case 'backup.snapshot.undelete':
      return 'A deleted backup was brought back.';
    case 'workbench.backup':
      return `A workbench’s settings were backed up${str(d, 'device') ? ` from ${str(d, 'device')}` : ''}${many(num(d, 'projects'), 'project') ? `, with ${many(num(d, 'projects'), 'project')}` : ''}.`;
    case 'workbench.backup.delete':
      return 'A person deleted their workbench settings backup.';
    case 'workbench.restore':
      return `A workbench’s settings were restored from a backup${str(d, 'via') === 'console' ? ' in the console' : ''}.`;

    // Billing and plan.
    case 'billing.checkout':
      return `Checkout was started for ${many(num(d, 'seats'), 'seat') ?? 'seats'}${d.trial === true ? ', as a trial' : ''}.`;
    case 'billing.refused':
      return `${str(d, 'role') === 'admin' ? 'An admin' : 'Someone who is not an owner'} was refused ${str(d, 'doing') === 'seats' ? 'a change of seats' : str(d, 'doing') === 'checkout' ? 'starting a plan' : 'the billing details'}: only the team’s owners change billing.`;
    case 'billing.seats':
      return `The number of seats changed${num(d, 'from') !== undefined ? ` from ${num(d, 'from')}` : ''}${num(d, 'to') !== undefined ? ` to ${num(d, 'to')}` : ''}.`;
    case 'billing.licence.redeem':
      return `A Teams licence was redeemed${num(d, 'seats') !== undefined ? ` for ${num(d, 'seats')} seats` : ''}${str(d, 'until') ? `, until ${str(d, 'until')!.slice(0, 10)}` : ''}.`;
    case 'support.request':
      return `A request for help was sent to support${str(d, 'subject') ? `: ${str(d, 'subject')}` : ''}.`;
    case 'operator.overview':
      return 'A platform operator read the overview of Mojo Up AI Cloud.';
    case 'operator.organisations':
      return 'A platform operator read the list of organisations.';
    case 'operator.remove.organisation':
      return `A platform operator removed the organisation${quoted(str(d, 'name'))}${str(d, 'reason') ? `: ${str(d, 'reason')}` : ''}.`;
    case 'operator.remove.person':
      return `A platform operator removed ${str(d, 'email') ?? 'a person'} from Mojo Up AI Cloud${str(d, 'reason') ? `: ${str(d, 'reason')}` : ''}.`;
    case 'organisation.plan':
      return `The plan changed${str(d, 'from') ? ` from ${str(d, 'from')}` : ''}${str(d, 'to') ? ` to ${str(d, 'to')}` : ''}${many(num(d, 'seats'), 'seat') ? `, with ${many(num(d, 'seats'), 'seat')}` : ''}.`;
    case 'licence.install':
      return `A licence${str(d, 'holder') ? ` for ${str(d, 'holder')}` : ''} was installed${d.replaced ? ', replacing the old one' : ''}.`;
    case 'licence.renew':
      return `The licence was renewed${str(d, 'exp') ? ` until ${str(d, 'exp')}` : ''}.`;

    // Board tasks.
    case 'board.task.create':
      return `${str(d, 'title') ? `The task “${str(d, 'title')}”` : 'A task'} was added to a board.`;
    case 'board.task.update':
      return `A board task was changed${str(d, 'title') ? `: “${str(d, 'title')}”` : ''}.`;
    case 'board.task.comment':
      return 'A comment was added to a board task.';
    case 'board.task.delete':
      return `${str(d, 'title') ? `The task “${str(d, 'title')}”` : 'A task'} was deleted from a board.`;
    case 'board.task.rollup':
      return 'A task’s progress was worked out again from the tasks it hands out.';

    // Budgets.
    case 'budget.create':
      return `The budget${quoted(str(d, 'name'))} was created${usd(num(d, 'amountUsd')) ? ` at ${usd(num(d, 'amountUsd'))}${str(d, 'period') ? ` a ${str(d, 'period')}` : ''}` : ''}.`;
    case 'budget.update':
      return `The budget${quoted(str(d, 'name'))} was changed.`;
    case 'budget.delete':
      return 'A budget was deleted.';
    case 'budget.changed':
      return `The budget${quoted(str(d, 'name'))} ${str(d, 'percent') ? `reached ${str(d, 'percent')}%` : 'changed state'}${str(d, 'state') ? ` and is now ${str(d, 'state')}` : ''}.`;
    // Incidents (Hermes item 3): repeated failures are one incident.
    case 'incident.filed':
      return `${target ?? 'Something'} failed${str(d, 'error') ? `: ${str(d, 'error')?.replace(/\.$/, '')}` : ''}. An incident was opened.`;
    case 'incident.repeated':
      return `${target ?? 'Something'} failed again${num(d, 'count') ? `, ${num(d, 'count')} times now` : ''}${str(d, 'error') ? `: ${str(d, 'error')?.replace(/\.$/, '')}` : ''}.`;
    case 'incident.reopened':
      return `${target ?? 'Something'} failed again after its incident was closed${str(d, 'error') ? `: ${str(d, 'error')?.replace(/\.$/, '')}` : ''}. The incident was reopened.`;
    case 'incident.acknowledged':
      return `The incident on ${target ?? 'a failing run'} was acknowledged${num(d, 'count') ? ` (${num(d, 'count')} ${num(d, 'count') === 1 ? 'failure' : 'failures'})` : ''}; it stays quiet for a day.`;
    case 'incident.closed':
      return `The incident on ${target ?? 'a failing run'} was closed.`;
    case 'budget.override':
      return `The budget hold on a project was lifted${str(d, 'hours') ? ` for ${str(d, 'hours')} hours` : ''}${why}`;
    case 'budget.refused':
      return `Work was stopped by the budget${quoted(str(d, 'budget'))}${str(d, 'percent') ? `, which is at ${str(d, 'percent')}%` : ''}.`;
    case 'organisation.budgets-fail-closed':
      return d.failClosed === true ? 'Budgets now stop work when they cannot be checked.' : 'Budgets no longer stop work when they cannot be checked.';

    // The catalogue: submissions, review and files.
    case 'catalogue.submit': {
      const scan = str(d, 'scan') === 'dangerous' ? ' The scan found something dangerous; it cannot be approved as it is.' : str(d, 'scan') === 'caution' ? ' The scan found something to look at.' : '';
      if (str(d, 'outcome') === 'pending') return `${target ?? 'An entry'} was submitted to the catalogue for approval${str(d, 'kind') ? ` (${str(d, 'kind')})` : ''}.${scan}`;
      return `${target ?? 'An entry'} was ${str(d, 'via') === 'administrator' ? 'published to the catalogue by an administrator' : 'published to the catalogue'}${str(d, 'kind') ? ` (${str(d, 'kind')})` : ''}.`;
    }
    case 'catalogue.verified': {
      const sentence = str(d, 'sentence')?.replace(/\.$/, '');
      return `The tool server ${target ?? ''} was started on ${str(d, 'device') ?? 'a node'} to see what it registers${sentence ? `: ${sentence}` : ''}.`.replace('server  was', 'server was');
    }
    case 'catalogue.archive':
      return `${target ?? 'An entry'} was archived from the catalogue${num(d, 'loads90d') === 0 ? ' (not loaded in 90 days)' : ''}; no workbench is offered it until it is restored.`;
    case 'catalogue.restore':
      return `${target ?? 'An entry'} was restored to the catalogue.`;
    case 'catalogue.pin':
      return `${target ?? 'A skill'} was pinned: it is never offered for archiving.`;
    case 'catalogue.unpin':
      return `${target ?? 'A skill'} was unpinned.`;
    case 'catalogue.approve':
      return `${target ?? 'A submission'} was approved for the catalogue.`;
    case 'catalogue.decline':
      return `${target ?? 'A submission'} was declined${why}`;
    case 'catalogue.evaluated': {
      const sentence = str(d, 'sentence')?.replace(/\.$/, '');
      const verdict = str(d, 'verdict') === 'pass' ? 'passed' : str(d, 'verdict') === 'fail' ? 'did not pass' : 'was graded';
      return `The skill ${target ?? ''} was evaluated${str(d, 'device') ? ` on ${str(d, 'device')}` : ''} and ${verdict}${sentence ? `: ${sentence}` : ''}.`.replace('skill  was', 'skill was');
    }
    case 'specialist.evaluated': {
      const sentence = str(d, 'sentence')?.replace(/\.$/, '');
      const verdict = str(d, 'verdict') === 'pass' ? 'passed' : str(d, 'verdict') === 'fail' ? 'did not pass' : 'was scored';
      const rate = num(d, 'passRate');
      const where = [str(d, 'dataset') ? ` against ${str(d, 'dataset')}` : '', str(d, 'device') ? ` on ${str(d, 'device')}` : ''].join('');
      return `${target ? `The specialist ${target}` : 'A specialist'} was evaluated${where} and ${verdict}${rate !== undefined ? ` (${Math.round(rate * 100)}% of cases passed)` : ''}${sentence ? `: ${sentence}` : ''}.`;
    }
    case 'foundry.publish.asked':
      return `${target ?? 'A Foundry member'} was asked to be published to Foundry${str(d, 'projectEndpoint') ? ` (${str(d, 'projectEndpoint')})` : ''}${str(d, 'device') ? ` by a node` : ''}.`;
    case 'foundry.unpublish.asked':
      return `A node was asked to delete the Foundry agent of ${target ?? 'a member'}.`;
    case 'foundry.block.recorded':
      return d.removed === true ? `A node recorded that the Foundry agent of ${target ?? 'a member'} was deleted.` : `A node recorded the Foundry agent of ${target ?? 'a member'}${str(d, 'agentVersion') ? `, version ${str(d, 'agentVersion')}` : ''}.`;
    // The organisation's approved Foundry connections, its Foundry agent catalogue and requests for approval (D441).
    case 'foundry.connection.save':
      return `The Foundry connection ${str(d, 'name') ?? target ?? ''} was saved${str(d, 'kind') === 'azure-local' ? ' (Azure Local)' : ''}; the approved Foundry projects were updated to match.`.replace('connection  was', 'connection was');
    case 'foundry.connection.delete':
      return `The Foundry connection ${str(d, 'name') ?? target ?? ''} was removed; the approved Foundry projects were updated to match.`.replace('connection  was', 'connection was');
    case 'foundry.connection.request':
      return `${str(d, 'project') ? `The Foundry project ${str(d, 'project')}` : `Foundry Local on ${str(d, 'cluster') ?? 'a cluster'}`} was put forward for approval.`;
    case 'foundry.connection.decide':
      return str(d, 'decision') === 'approve' ? `A request to approve a Foundry project was approved and added to the organisation’s connections.` : `A request to approve a Foundry project was declined.`;
    // The organisation's approved Kubernetes clusters and requests for approval (D447).
    case 'compute.cluster.save':
      return `The cluster ${str(d, 'name') ?? target ?? ''} was saved${num(d, 'pools') ? ` with ${num(d, 'pools')} node pool${num(d, 'pools') === 1 ? '' : 's'}` : ''}; the approved clusters in the policy were updated to match.`.replace('cluster  was', 'cluster was');
    case 'compute.cluster.delete':
      return `The cluster ${str(d, 'name') ?? target ?? ''} was removed; the approved clusters in the policy were updated to match.`.replace('cluster  was', 'cluster was');
    case 'compute.cluster.request':
      return `The cluster ${str(d, 'name') ?? 'a cluster'} was put forward for approval.`;
    case 'compute.cluster.decide':
      return str(d, 'decision') === 'approve' ? `A request to approve the cluster ${str(d, 'name') ?? ''} was approved and the cluster added.`.replace('cluster  was', 'cluster was') : `A request to approve the cluster ${str(d, 'name') ?? ''} was declined.`.replace('cluster  was', 'cluster was');
    case 'foundry.catalogue.publish':
      return denied ? `Publishing a Foundry agent was refused${a.error ? `: ${a.error}` : ''}.` : `The Foundry agent ${str(d, 'name') ?? target ?? ''} was published as version ${str(d, 'version') ?? 'a new one'}${str(d, 'state') === 'held' ? ' and is waiting for review' : ''}.`.replace('agent  was', 'agent was');
    case 'foundry.catalogue.review':
      return `A version of the Foundry agent ${str(d, 'name') ?? target ?? ''} was ${str(d, 'decision') === 'approve' ? 'approved and listed' : 'rejected'}.`.replace('agent  was', 'agent was');
    case 'foundry.catalogue.withdraw':
      return `The Foundry agent ${str(d, 'name') ?? target ?? ''} was withdrawn from the catalogue.`.replace('agent  was', 'agent was');
    case 'foundry.catalogue.install':
      return `The Foundry agent ${str(d, 'name') ?? target ?? ''} was added${str(d, 'as') ? ` as a ${str(d, 'as')}` : ''}.`.replace('agent  was', 'agent was');
    case 'catalogue.asset.upload':
      return `The file ${target ?? 'a file'} was uploaded to the catalogue.`;
    case 'catalogue.assets.swept':
      return `${many(num(d, 'count'), 'unused catalogue file') ?? 'Unused catalogue files'} ${num(d, 'count') === 1 ? 'was' : 'were'} cleared out.`;
    case 'catalogue.design-system.import':
      return `${target ?? 'A design system'} was imported${d.replaces ? ', replacing the earlier one' : ''}.`;
    case 'catalogue.design-system.publish':
      return `${target ?? 'A design system'} was published${str(d, 'delivery') ? `, delivered by ${plain(str(d, 'delivery'))}` : ''}.`;
    case 'catalogue.design-system.review':
      return `${target ?? 'A design system'} was reviewed${num(d, 'findings') !== undefined ? `: ${many(num(d, 'findings'), 'finding') ?? ''}` : ''}.`;
    case 'catalogue.design-system.reviews-pruned':
      return `${many(num(d, 'count'), 'old design system review') ?? 'Old design system reviews'} ${num(d, 'count') === 1 ? 'was' : 'were'} cleared out.`;
    case 'catalogue.template.use':
      return `A design system${quoted(target)} was started from a template.`;
    case 'organisation.catalogue-submissions':
      return `Who may submit to the catalogue was changed${str(d, 'setting') ? ` to ${plain(str(d, 'setting'))}` : ''}.`;

    // Chargeback and invoices.
    case 'chargeback.close':
      return `The chargeback for ${monthText(target ?? str(d, 'month'))} was closed${usd(num(d, 'usd')) ? ` at ${usd(num(d, 'usd'))}` : ''}.`;
    case 'chargeback.send':
      return d.ok === false ? `The chargeback for ${monthText(target ?? str(d, 'month'))} could not be sent.` : `The chargeback for ${monthText(target ?? str(d, 'month'))} was sent${many(num(d, 'recipients'), 'person', 'people') ? ` to ${many(num(d, 'recipients'), 'person', 'people')}` : ''}.`;
    case 'organisation.chargeback-schedule':
      return d.cleared === true ? 'The monthly chargeback schedule was cleared.' : `The monthly chargeback schedule was set${num(d, 'dayOfMonth') ? ` for day ${num(d, 'dayOfMonth')} of the month` : ''}.`;
    case 'invoice.upload':
      return `An invoice, ${target ?? 'a provider’s invoice'}, was uploaded.`;
    case 'invoice.delete':
      return `The invoice ${target ?? 'for a month'} was deleted.`;

    // Cloud agents.
    case 'cloud.run.start':
      return `A cloud agent run was started${str(d, 'provider') ? ` on ${vendorText(str(d, 'provider'))}` : ''}.`;
    case 'cloud.run.end':
      return `A cloud agent run ended${str(d, 'reason') ? ` (${plain(str(d, 'reason'))})` : ''}${many(num(d, 'calls'), 'tool call') ? ` after ${many(num(d, 'calls'), 'tool call')}` : ''}.`;
    case 'cloud.run.refused':
      return `A cloud agent run was refused${str(d, 'provider') ? ` on ${vendorText(str(d, 'provider'))}` : ''} by the organisation’s rules.`;
    case 'cloud.mcp.unauthorised':
      return 'A cloud agent run was turned away from its tools: it used the wrong credential.';

    // Conversations kept with a project.
    case 'conversation.put':
      return `${str(d, 'title') ? `The conversation “${str(d, 'title')}”` : 'A conversation'} was saved to the project.`;
    case 'conversation.patch':
      return 'A project conversation was updated.';
    case 'conversation.remove':
      return 'A conversation was removed from the project.';
    case 'conversation.messages':
      return `${many(num(d, 'accepted'), 'message') ?? 'Messages'} ${num(d, 'accepted') === 1 ? 'was' : 'were'} added to a project conversation.`;
    case 'conversation.audience':
      return str(d, 'to') === 'project' ? 'A conversation was shared with the project.' : str(d, 'to') === 'members' ? `A conversation was shared with ${num(d, 'members') === 1 ? '1 person' : `${num(d, 'members') ?? 'some'} people`}.` : 'A conversation was made private again.';
    case 'conversation.share':
      return `${many(num(d, 'messages'), 'message') ?? 'A conversation'} ${num(d, 'messages') === undefined ? 'was' : num(d, 'messages') === 1 ? 'was' : 'were'} shared from a project conversation.`;

    case 'teamwork.message': {
      // People in Team Work: who wrote, what for, whom it named; never the words.
      const who = { person: 'A person', agent: 'An agent', system: 'The workbench' }[str(d, 'authorKind') ?? ''] ?? 'Someone';
      const what = str(d, 'intent') === 'steer' ? 'steered a Team Work session' : str(d, 'intent') === 'answer' ? `answered a question in a Team Work session${d.answeredFor ? ' for someone they cover' : ''}` : 'wrote in a Team Work session';
      const named = num(d, 'mentions');
      const extras = [named ? `mentioning ${named === 1 ? '1 person' : `${named} people`}` : undefined, d.asks ? 'asking someone a question' : undefined].filter(Boolean);
      return `${who} ${what}${extras.length ? `, ${extras.join(' and ')}` : ''}.`;
    }
    case 'people.availability':
      return d.away ? 'Someone marked themselves away, so the people who cover them can answer for them.' : 'Someone marked themselves back.';
    case 'people.display':
      return 'Someone changed how dates and times are shown to them.';
    case 'me.apps.save':
      return 'Someone saved one of their own apps.';
    case 'me.apps.delete':
      return 'Someone deleted one of their own apps.';
    case 'me.home.save':
      return 'Someone changed how their Home is arranged.';

    // Spending and pricing.
    case 'cost.anomaly':
      return `Spending looked unusual${str(d, 'model') ? ` on ${str(d, 'model')}` : ''}${num(d, 'times') ? `: about ${num(d, 'times')} times the usual` : ''}.`;
    case 'pricing.entry':
      return `The price of ${str(d, 'model') ?? 'a model'}${str(d, 'provider') ? ` from ${vendorText(str(d, 'provider'))}` : ''} was changed.`;
    case 'pricing.reprice':
      return `Past spending was priced again${str(d, 'since') ? ` from ${str(d, 'since')}` : ''}.`;
    case 'pricing.update':
      return 'The price list and seat prices were changed.';
    case 'organisation.currency':
      return `The organisation’s currency is now ${str(d, 'currency') ?? 'US dollars'}.`;
    case 'platform.capacity.crossed':
      return `The platform passed a capacity limit${str(d, 'lines') ? `: ${str(d, 'lines')}` : ''}.`;
    case 'platform.capacity.cleared':
      return 'The platform is back within its capacity limits.';
    case 'usage.refiled':
      return `${many(num(d, 'count'), 'record of use') ?? 'Records of use'} ${num(d, 'count') === 1 ? 'was' : 'were'} filed under the right project.`;
    case 'organisation.usage-retention':
      return `Usage is now kept for ${num(d, 'days') ?? 'a set number of'} days.`;

    // Decisions and plans.
    case 'decision.ask':
      return `A decision was asked of the project${d.blocking === true ? ', and it blocks work' : ''}.`;
    case 'decision.update':
      return 'A decision of the project was changed.';
    case 'decision.answer':
      return `A decision of the project was answered${d.provisional === true ? ', for now' : ''}.`;
    case 'plan.create':
      return `${str(d, 'ref') ?? 'A plan'} was created.`;
    case 'plan.update':
      return `${str(d, 'ref') ?? 'A plan'} was updated${str(d, 'from') && str(d, 'status') ? `, from ${plain(str(d, 'from'))} to ${plain(str(d, 'status'))}` : ''}.`;
    case 'plan.acceptance':
      return `${str(d, 'ref') ?? 'A plan'}’s acceptance was ${str(d, 'verdict') ? `marked ${plain(str(d, 'verdict'))}` : 'recorded'}${str(d, 'note') ? `: ${str(d, 'note')}` : '.'}`;
    case 'plan.wrap-up.ask': {
      const words = typeof d.action === 'string' ? wrapUpAskWords(d as unknown as PlanWrapUpAskInput) : 'answered a step';
      return `On a plan’s wrap-up: ${words[0]!.toLowerCase()}${words.slice(1)}.`;
    }
    case 'plan.number':
      return `${str(d, 'ref') ?? 'A plan'} was numbered${d.kept === false ? ', and the number asked for was already taken' : ''}.`;
    case 'plan.remove':
      return `${str(d, 'ref') ?? 'A plan'} was removed.`;

    // Dispatching work to machines.
    case 'task.dispatch':
      return denied ? `${str(d, 'title') ?? 'A task'} could not be sent to a machine${str(d, 'error') ? `: ${str(d, 'error')}` : ''}.` : `${str(d, 'title') ?? 'A task'} was sent to a machine${d.takeover === true ? ' after being taken over' : ''}.`;
    case 'task.dispatch.ack':
      return denied || d.ok === false ? `A machine did not take up a task sent to it${str(d, 'message') ? `: ${str(d, 'message')}` : ''}.` : 'A machine took up a task sent to it.';
    case 'member.dispatch':
      return denied ? `A team member could not be started on a machine${str(d, 'error') ? `: ${str(d, 'error')}` : ''}.` : 'A team member was started on a machine.';
    case 'member.dispatch.ack':
      return denied || d.ok === false ? 'A machine did not start a team member sent to it.' : 'A machine started a team member sent to it.';
    case 'team.placed':
      return `A team was placed on ${many(len(d, 'placements'), 'machine') ?? 'machines'}${num(d, 'refused') ? `; ${num(d, 'refused')} refused` : ''}.`;
    case 'run.fallback':
      return `A turn${target ? ` of ${target}` : ''} finished on a backup model${str(d, 'model') ? `, ${str(d, 'model')}` : ''}${str(d, 'reason') ? ` (the first was ${str(d, 'reason')})` : ''}.`;
    case 'run.stop':
      return 'A run was stopped.';

    // Remote control of a live session (D282).
    case 'remote.send':
    case 'remote.interrupt':
    case 'remote.stop':
    case 'remote.approve':
    case 'remote.deny':
    case 'remote.session.on':
    case 'remote.session.off':
    case 'remote.board.pause':
    case 'remote.board.resume':
    case 'remote.handoff':
    case 'remote.press':
    case 'remote.session.end-all':
      return remoteSentence(a.action, d, denied, a.error);

    // A node managed from the console or the phone (workbench D290), on the node's own chain.
    case 'node.remote.watch':
    case 'node.remote.approve':
    case 'node.remote.deny':
    case 'node.remote.stop-member':
    case 'node.remote.restart':
    case 'node.remote.update-check':
    case 'node.remote.update-install':
    case 'node.remote.service':
    case 'node.remote.update':
    case 'node.remote.routine':
    case 'node.remote.sessions':
    case 'node.remote.setting':
      return nodeRemoteSentence(a.action, d, denied, a.error);

    // Foundry agents a workbench publishes for a team's Foundry members (workbench D273 round C).
    case 'foundry.agent.publish': {
      const member = str(d, 'member', 'memberName', 'agentName') ?? 'a team member';
      const version = str(d, 'version', 'agentVersion');
      if (denied) return `Publishing the Foundry agent for ${member} was refused${a.error ? `: ${a.error}` : ''}.`;
      return `The Foundry agent for ${member} was ${str(d, 'result') === 'converged' ? 'brought up to date' : 'published'}${version ? ` as version ${version}` : ''}.`;
    }
    case 'foundry.agent.delete':
      return denied ? `Deleting the Foundry agent for ${str(d, 'member', 'memberName') ?? 'a team member'} was refused${a.error ? `: ${a.error}` : ''}.` : `The Foundry agent for ${str(d, 'member', 'memberName') ?? 'a team member'} was deleted from its Foundry project.`;
    case 'foundry.agent.unlink':
      return `The Foundry agent for ${str(d, 'member', 'memberName') ?? 'a team member'} was unlinked from the team; the agent stays in Foundry.`;
    case 'foundry.agent.prune': {
      const n = num(d, 'pruned') ?? num(d, 'versions');
      return `${many(n, 'old version') ?? 'Old versions'} of the Foundry agent for ${str(d, 'member', 'memberName') ?? 'a team member'} ${n === 1 ? 'was' : 'were'} removed.`;
    }

    // Devices and nodes.
    case 'device.revoke':
      return `${str(d, 'hostname') ?? 'A machine'} was revoked${many(num(d, 'tokens'), 'token') ? `: ${many(num(d, 'tokens'), 'token')} ended` : ''}.`;
    case 'device.clear.request':
      return `${str(d, 'hostname') ?? 'A machine'} was asked to delete the organisation's data${str(d, 'scope') === 'organisation-and-repositories' ? ' and its repositories' : ''}${d.remove === true ? ', and removed' : ''}${str(d, 'reason') ? `: ${str(d, 'reason')}` : ''}.`;
    case 'device.clear.done':
      return `A machine cleared the organisation's data${str(d, 'message') ? `: ${str(d, 'message')}` : ''}.`;
    case 'device.clear.failed':
      return `A machine could not clear all of the organisation's data${str(d, 'message') ? `: ${str(d, 'message')}` : ''}.`;
    case 'device.scopes': {
      const added = Array.isArray(d.added) ? scopeRequestWords(d.added.filter((s): s is string => typeof s === 'string')) : '';
      if (d.requested === true) return added ? `A machine was allowed ${added}, as it asked.` : 'A machine was allowed what it asked for.';
      return 'A machine’s permissions were changed.';
    }
    case 'device.scopes.requested': {
      const asked = Array.isArray(d.scopes) ? scopeRequestWords(d.scopes.filter((s): s is string => typeof s === 'string')) : '';
      return `A machine asked to be allowed ${asked || 'more remote access'}.`;
    }
    case 'policy.exception.request':
      return `Someone asked for an exception for ${target ?? 'something the policy refused'}${num(d, 'hours') ? ` ${exceptionDurationWords(num(d, 'hours')!)}` : ''}.`;
    case 'policy.exception.approve':
      return `An exception for ${target ?? 'something the policy refused'} was approved${num(d, 'approvedHours') ? ` ${exceptionDurationWords(num(d, 'approvedHours')!)}` : ''}.`;
    case 'policy.exception.decline':
      return `A request for an exception for ${target ?? 'something the policy refused'} was declined.`;
    case 'policy.exception.withdraw':
      return `A request for an exception for ${target ?? 'something the policy refused'} was withdrawn.`;
    case 'policy.exception.revoke':
      return `An exception for ${target ?? 'something the policy refused'} was ended early.`;
    case 'organisation.exception-requests':
      return d.on === false ? 'Requests for exceptions were turned off for the organisation.' : 'Requests for exceptions were turned on for the organisation.';
    case 'device.scopes.refused':
      return 'A machine’s ask for more remote access was answered Not now.';
    case 'device.scopes.request.expired':
      return 'A machine’s ask for more remote access lapsed unanswered.';
    case 'routine-events.scheduled': {
      const n = num(d, 'orders') ?? 0;
      return `A timetable came round for ${str(d, 'rule') ?? 'a rule'}${target ? ` on ${target}` : ''}: ${n ? `${n} machine${n === 1 ? ' was' : 's were'} asked to start it` : 'no machine could be asked'}.`;
    }
    case 'routine-events.received': {
      const n = num(d, 'orders') ?? 0;
      const what = str(d, 'summary');
      return `Your organisation forwarded an event from ${target ?? 'a service'}${what ? `: ${what}` : ''}. ${n ? `${n} ${n === 1 ? 'machine was' : 'machines were'} asked to start a routine.` : 'It started nothing.'}`;
    }
    case 'routine-events.refused': {
      const why = ROUTINE_EVENT_REFUSAL_WORDS[str(d, 'reason') as RoutineEventRefusal];
      return `Something sent to ${target ?? 'a connected service'}’s address was not taken${why ? `: ${why}` : ''}.`;
    }
    case 'routine-events.source.create':
      return `${ROUTINE_EVENT_SERVICES[str(d, 'service') as OutsideEventSource]?.name ?? 'A service'} was connected as ${target ?? 'a new service'}, to start routines when something happens.`;
    case 'routine-events.source.update':
      return `What ${target ?? 'a connected service'} starts when something happens was changed.`;
    case 'routine-events.source.secret':
      return `${target ?? 'A connected service'} was given a new secret.`;
    case 'routine-events.source.remove':
      return `${target ?? 'A connected service'} was removed: its address stops listening.`;
    case 'project.orders.refused':
      return str(d, 'refused') === 'nodes'
        ? 'Asking the project’s nodes to do something was refused: an administrator did not assign them to the project.'
        : `Asking the project’s nodes to ${str(d, 'kind') ?? 'do something'} was refused: that needs an administrator.`;
    case 'device.order':
      return orderSentence(str(d, 'orderKind', 'kind'), str(d, 'name', 'key', 'id'), str(d, 'state', 'outcome'), str(d, 'message'));
    case 'node.approve':
      return `${str(d, 'name') ?? 'A node'} was approved to join.`;
    case 'node.refuse':
      return `${target ?? 'A node'} was refused.`;
    case 'node.assign':
      return denied ? 'Assigning a node was refused: the node is not the project’s own, so it needs an administrator.' : 'A node was given work to do for a person or project.';
    case 'node.unassign':
      return 'A node’s assignment was taken away.';
    case 'node.update':
      return 'A node’s settings were changed.';
    case 'node.join':
      return `${str(d, 'name') ?? 'A node'} asked to join${str(d, 'surface') === 'pending-surface' ? ' and waits for its abilities to be approved' : ''}.`;
    case 'node.join.claim':
      return `${target ?? 'A node'} was claimed by the person who set it up.`;
    case 'node.joinkey.create':
      return `The join key${quoted(str(d, 'name'))} was made${str(d, 'via') === 'github' ? ` for a GitHub run of ${str(d, 'repository') ?? 'a repository'}` : ''}${str(d, 'project') ? ' for one project' : ''}${str(d, 'nodePool') ? ` and the node pool ${str(d, 'nodePool')}` : ''}${d.ephemeral === true ? ' for short-lived nodes' : ''}${d.reusable === true ? ' and can be used again' : ''}.`;
    case 'node.joinkey.revoke':
      return 'A join key was revoked.';
    case 'node.ephemeral.remove':
      return 'A short-lived node was removed after it went quiet.';
    case 'node.peertoken':
      return `A node was refused a token to reach ${target ?? 'another service'}.`;
    case 'node.surface.approve':
      return 'A node’s new abilities were approved.';
    case 'node.surface.refuse':
      return 'A node’s new abilities were refused.';
    case 'update.cancel':
      return `${str(d, 'product') ? `The ${str(d, 'product')} update` : 'An update'} order for a machine was cancelled.`;
    case 'update.order':
      return `${str(d, 'product') ?? 'A product'} was ordered to ${str(d, 'action') ?? 'update'}${str(d, 'version') ? ` to ${str(d, 'version')}` : ''}${num(d, 'machines') !== undefined ? ` on ${many(num(d, 'machines'), 'machine')}` : ' on a machine'}${why}`;
    case 'update.rollback':
      return `${str(d, 'product') ?? 'A product'} was ordered back to ${str(d, 'version') ?? 'an earlier version'}${num(d, 'machines') !== undefined ? ` on ${many(num(d, 'machines'), 'machine')}` : ' on a machine'}${why}`;

    // Memory.
    case 'memory.clearances':
      return 'Who may read which memory labels was changed.';
    case 'memory.compartment.put':
      return `The memory compartment${quoted(str(d, 'name') ?? target)} was saved.`;
    case 'memory.compartment.delete':
      return 'A memory compartment was deleted.';
    case 'memory.grants':
      return 'The memory a person may read was changed.';
    case 'memory.rules':
      return `The memory rules were changed${many(num(d, 'rules'), 'rule') ? ` (${many(num(d, 'rules'), 'rule')})` : ''}.`;
    case 'memory.ingest': {
      // What the push was, in one line: the repository (its root), the revision, what moved, and whether it was a batch of a rebuild.
      const up = num(d, 'upserts');
      const gone = num(d, 'removed');
      const parts = [up !== undefined && up > 0 ? `${up.toLocaleString()} ${up === 1 ? 'item' : 'items'} added or changed` : undefined, gone ? `${gone.toLocaleString()} removed` : undefined, num(d, 'masked') ? `secrets hidden in ${num(d, 'masked')}` : undefined, num(d, 'vectors') ? `${num(d, 'vectors')!.toLocaleString()} ready to be found by meaning` : undefined].filter(Boolean);
      const rev = str(d, 'revision');
      return `The memory of${quoted(str(d, 'rootId'))}${rev ? ` at ${rev.length > 12 ? rev.slice(0, 7) : rev}` : ''} was ${d.full === true ? 'rebuilt' : 'updated'}${parts.length ? `: ${parts.join(', ')}` : ': nothing changed'}${d.full === true && d.done !== true ? ' (one batch of the rebuild)' : ''}.`;
    }
    case 'memory.proposal.created':
    case 'memory.proposal.joined':
    case 'memory.proposal.accepted':
    case 'memory.proposal.rejected':
    case 'memory.proposal.stale':
    case 'memory.proposal.expired':
    case 'memory.proposal.escalated': {
      // Counts and who, never content: "3 lessons and 1 decision from Rey on task T-12".
      const kinds = (d.kinds && typeof d.kinds === 'object' ? d.kinds : {}) as Record<string, number>;
      const parts = Object.entries(kinds).map(([k, n]) => `${n} ${n === 1 ? k : `${k}s`}`);
      const what = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : (parts[0] ?? 'memory');
      const p = (d.proposer && typeof d.proposer === 'object' ? d.proposer : {}) as Record<string, unknown>;
      const who = typeof p.actor === 'string' ? ` from ${p.actor.replace(/\s*\(.*\)\s*$/, '')}` : '';
      const task = typeof p.task === 'string' ? ` on task ${p.task}` : '';
      const by = str(d, 'manager') ? ' by the operations manager' : '';
      const verb: Record<string, string> = {
        'memory.proposal.created': 'proposed for review',
        'memory.proposal.joined': 'added to a proposal waiting for review',
        'memory.proposal.accepted': `accepted into memory${by}`,
        'memory.proposal.rejected': `kept out of memory${by}`,
        'memory.proposal.stale': 'not taken: it changed after it was proposed',
        'memory.proposal.expired': 'not decided in 14 days, so it expired',
        'memory.proposal.escalated': 'handed by the operations manager to a person',
      };
      return `${what.charAt(0).toUpperCase()}${what.slice(1)}${who}${task} ${parts.length === 1 && Object.values(kinds)[0] === 1 ? 'was' : 'were'} ${verb[a.action] ?? 'changed'}.`;
    }
    case 'memory.embed': {
      const n = num(d, 'accepted') ?? 0;
      return `${n.toLocaleString()} memory ${n === 1 ? 'item was' : 'items were'} made ready to be found by meaning${num(d, 'stale') ? `; ${many(num(d, 'stale'), 'item')} had changed and waits for the next pass` : ''}.`;
    }
    case 'memory.write':
      return `A ${str(d, 'kind') ?? 'memory'} item was written to memory${num(d, 'masked') ? ', with secrets hidden' : ''}.`;
    case 'memory.read':
      return `Project memory was read${str(d, 'via') ? ` through ${plain(str(d, 'via'))}` : ''}${num(d, 'itemsFiltered') ? `; ${many(num(d, 'itemsFiltered'), 'item')} left out` : ''}.`;
    case 'memory.relabel':
      return `A memory item was relabelled${d.lowered === true ? ' to a lower sensitivity' : ''}.`;
    case 'memory.purge':
      return 'A memory item was erased everywhere it was kept.';
    case 'memory.knowledge.write': {
      const n = (num(d, 'created') ?? 0) + (num(d, 'updated') ?? 0);
      return `${n ? `${many(n, 'piece')} of knowledge ${n === 1 ? 'was' : 'were'} written to memory` : 'Knowledge was sent to memory, unchanged'}${num(d, 'superseded') ? `; ${many(num(d, 'superseded'), 'older one')} replaced` : ''}.`;
    }
    case 'memory.knowledge.tidy':
      return `Memory was tidied: ${many(num(d, 'answers') ?? 0, 'answered question')} and ${many(num(d, 'summaries') ?? 0, 'summary', 'summaries')} up to date${num(d, 'dropped') ? `, ${many(num(d, 'dropped'), 'empty note')} left out` : ''}.`;
    case 'memory.promote':
      return `${many(num(d, 'items'), 'memory item') ?? 'Memory items'} ${num(d, 'items') === 1 ? 'was' : 'were'} shared with the whole organisation.`;
    case 'memory.mirror':
      return 'The project’s memory mirror settings were changed.';
    case 'memory.propagate.failed':
      return `A memory change did not reach every machine${str(d, 'what') ? ` (${str(d, 'what')})` : ''}.`;
    case 'memory.microsoft365':
      return `Microsoft 365 memory answered a question${many(num(d, 'citations'), 'source') ? `, with ${many(num(d, 'citations'), 'source')}` : ''}.`;
    case 'memory.microsoft365.enable':
      return 'Microsoft 365 memory was turned on.';
    case 'memory.microsoft365.disable':
      return 'Microsoft 365 memory was turned off.';
    case 'memory.microsoft365.workbench-client':
      return target && target !== 'none' ? 'The workbench’s Microsoft 365 sign-in was set.' : 'The workbench’s Microsoft 365 sign-in was cleared.';

    // The private network (mesh).
    case 'mesh.node.register':
      return `${str(d, 'name') ?? 'A machine'} joined the private network.`;
    case 'mesh.node.remove':
      return `${str(d, 'name') ?? 'A machine'} was removed from the private network.`;
    case 'mesh.node.rename':
      return `${str(d, 'from') ?? 'A machine'} was renamed${str(d, 'to') ? ` to ${str(d, 'to')}` : ''}.`;
    case 'mesh.node.tags':
      return `The tags of a machine on the private network were changed${len(d, 'notAllowed') ? `; ${many(len(d, 'notAllowed'), 'tag')} not allowed` : ''}.`;
    case 'mesh.node.disable':
      return `${str(d, 'name') ?? 'A machine'} was cut off from the private network.`;
    case 'mesh.node.enable':
      return `${str(d, 'name') ?? 'A machine'} was let back onto the private network.`;
    case 'mesh.node.key-expiry':
      return `${str(d, 'name') ?? 'A machine'}’s key ${str(d, 'keyExpiry') === 'never' ? 'no longer expires' : 'expires on the default schedule again'}.`;
    case 'mesh.key.rotate':
      return `The key for ${str(d, 'name') ?? 'a machine'} was rotated.`;
    case 'mesh.key.rejected':
      return `A key for ${target ?? 'a machine'} was refused.`;
    case 'mesh.key.expire':
    case 'mesh.key.expired':
      return `The key for ${str(d, 'name') ?? 'a machine'} expired.`;
    case 'mesh.key.expiring':
      return `The key for ${str(d, 'name') ?? 'a machine'} expires within a day.`;
    case 'mesh.flow.refused':
      return `${str(d, 'node') ?? 'A machine'} was stopped from reaching ${str(d, 'peer') ?? 'another machine'}${str(d, 'port') ? ` on port ${str(d, 'port')}` : ''}${num(d, 'refused') ? ` ${many(num(d, 'refused'), 'time')}` : ''}.`;
    case 'mesh.policy.update':
      return `The private network’s rules were changed${many(num(d, 'rules'), 'rule') ? ` (${many(num(d, 'rules'), 'rule')})` : ''}.`;

    // Notifications.
    case 'notification.file':
      return `${str(d, 'title') ? `“${str(d, 'title')}”` : 'A notice'} was filed${str(d, 'attention') === 'for-you' ? ' for a person' : ''}.`;
    case 'notification.update':
      return `A notice was marked ${str(d, 'state') ? plain(str(d, 'state')) : 'changed'}.`;
    case 'notification.done':
      return 'A notice was closed because what it asked for was done.';
    case 'notification.act':
      return 'A person acted on a notice.';
    case 'notification.state':
      return `A notice was marked ${str(d, 'state') ? plain(str(d, 'state')) : 'changed'} by its owner.`;
    case 'notification.push-settings':
      return 'The settings for notifications on phones were changed.';

    // The organisation itself.
    case 'organisation.create':
      return `The organisation${quoted(target)} was created.`;
    case 'organisation.rename':
      return `The organisation was renamed${str(d, 'from') ? ` from ${str(d, 'from')}` : ''}${target ? ` to ${target}` : ''}.`;
    case 'organisation.profile':
      return 'The organisation’s description and vision were changed.';
    case 'organisation.time-zone':
      return `The organisation’s time zone is now ${str(d, 'timeZone') ?? 'changed'}.`;
    case 'organisation.project-auto-archive':
      return `Quiet projects are now archived after ${num(d, 'days') ?? 'a set number of'} days.`;
    case 'organisation.projects-members-register':
      return d.on === false ? 'Only administrators may register new projects now.' : 'Members may register new projects again.';
    case 'organisation.step-up':
      return `The recent sign-in needed for sensitive actions is now ${num(d, 'minutes') ?? 'a set number of'} minutes.`;
    case 'organisation.quotas.update':
      return 'The organisation’s limits were changed.';
    case 'organisation.quota.limit':
      return `A limit was reached${plain(target) ? `: ${plain(target)}` : ''}, and the action was refused.`;
    case 'organisation.storage.alert':
      return `Storage reached ${num(d, 'level') ?? 'a set share of'}% of its limit${plain(target) ? ` for ${plain(target)}` : ''}.`;
    case 'organisation.export.start':
      return 'An export of the whole organisation was started.';
    case 'organisation.export.download':
      return 'The export of the organisation was downloaded.';
    case 'organisation.import':
      return `An organisation was imported${target ? ` as ${target}` : ''}${d.replace === true ? ', replacing what was there' : ''}.`;
    case 'organisation.invite':
      return `${target ?? 'A person'} was invited to the organisation${str(d, 'role') ? ` as ${str(d, 'role')}` : ''}.`;
    case 'organisation.invite.revoke':
      return `The invitation to ${target ?? 'a person'} was withdrawn.`;
    case 'organisation.keys.rotate':
      return `The organisation’s encryption key was rotated${target ? ` to ${target}` : ''}.`;
    case 'organisation.keys.reseal':
      return failed ? `Re-sealing the organisation’s secrets under the new key left ${many(num(d, 'failed'), 'secret') ?? 'some secrets'} unchanged.` : `${many(num(d, 'resealed'), 'secret') ?? 'Secrets'} ${num(d, 'resealed') === 1 ? 'was' : 'were'} sealed again under the new key.`;
    case 'organisation.keys.rewrap-master':
      return 'The organisation’s keys were wrapped again under a new master key.';
    case 'people.search':
      return 'A person searched for people.';
    case 'records.purge':
      return `A person’s records were erased${many(num(d, 'records'), 'record') ? ` (${many(num(d, 'records'), 'record')})` : ''}.`;
    case 'roles.update':
      return 'The roles of the organisation were changed.';
    // Roles and permissions (docs/rbac.md): every definition and assignment change.
    case 'role.define':
      return `The role${quoted(str(d, 'name'))} was made.`;
    case 'role.update':
      return `The role${quoted(str(d, 'name'))} was changed.`;
    case 'role.delete':
      return `The role${quoted(str(d, 'name'))} was removed${num(d, 'assignmentsRemoved') ? `, and ${many(num(d, 'assignmentsRemoved'), 'person, group or agent', 'people, groups or agents')} no longer ${num(d, 'assignmentsRemoved') === 1 ? 'holds' : 'hold'} it` : ''}.`;
    case 'role.assign':
      return `${str(d, 'role') ?? 'A role'} was given${(d.scope as { type?: string } | undefined)?.type === 'organisation' ? ' across the organisation' : ` in one ${(d.scope as { type?: string } | undefined)?.type ?? 'place'}`}.`;
    case 'role.revoke':
      return `${str(d, 'role') ?? 'A role'} was taken away.`;
    case 'role.observed':
      return d.roleId === 'platform-administrator' ? `Someone new holds the Platform Administrator role${d.source === 'assigned' ? ', given on Roles and permissions' : d.source === 'cloud-role' ? ', as an owner or admin' : ', from the directory'}.` : 'A sign-in brought different roles from the directory.';
    case 'role.break_glass':
      return 'Someone on the break-glass list signed in as an administrator.';
    // Agents as principals (docs/rbac.md step 6): the claim refused, and actions held for a person.
    case 'actor.refused':
      return d.reason === 'actor_unknown' ? 'A machine spoke for an agent the organisation does not have, and was refused.' : d.reason === 'actor_team_mismatch' ? 'A machine named an agent with the wrong team, and was refused.' : 'A machine spoke for an agent of a team that does not run on it, and was refused.';
    case 'action.held':
      return 'An agent’s action was held for a person to approve; nothing ran.';
    case 'action.approved':
      return 'A person approved an agent’s held action, to run once.';
    case 'action.rejected':
      return 'A person rejected an agent’s held action; it will not run.';
    case 'action.released':
      return 'An approved action of an agent ran, once.';
    case 'action.release.refused':
      return 'A machine sent an approval for a different request, and was refused.';

    // Portfolio: business areas and teams.
    case 'portfolio.area.create':
      return `The business area${quoted(str(d, 'name'))} was created.`;
    case 'portfolio.area.update':
      return `The business area${quoted(str(d, 'name'))} was changed.`;
    case 'portfolio.area.delete':
      return 'A business area was deleted.';
    case 'portfolio.managers':
      return 'Who manages the portfolio was changed.';
    case 'team.create':
      return `The team${quoted(str(d, 'name'))} was created.`;
    case 'team.update':
      return `The team${quoted(str(d, 'name'))} was changed.`;
    case 'team.delete':
      return 'A team was deleted.';
    case 'team.used':
      return `The ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} is now used by ${str(d, 'projectName') ?? 'another project'}.`;
    case 'team.unused':
      return `The ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} is no longer used by ${str(d, 'projectName') ?? 'a project'}.`;
    case 'team.merged': {
      const n = Array.isArray(d.projects) ? d.projects.length : 0;
      const moved = n ? many(n, 'project') : undefined;
      return `A copy of the ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} was merged into it and removed${moved ? `; ${moved} moved with it` : ''}.`;
    }
    case 'team.owner.set':
      return `The ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} is now owned by ${str(d, 'toName') ?? 'another person'}${str(d, 'fromName') ? `, handed on from ${str(d, 'fromName')}` : ''}.`;
    case 'team.proposal.create':
      return `A change to the ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} was proposed to its owner${str(d, 'ownerName') ? `, ${str(d, 'ownerName')}` : ''}.`;
    case 'team.proposal.accept':
      return `A proposed change to the ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} was accepted${num(d, 'version') ? `; it is at version ${num(d, 'version')}` : ''}.`;
    case 'team.proposal.decline':
      return `A proposed change to the ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} was declined${str(d, 'reason') ? `: ${str(d, 'reason')}` : ''}.`;
    case 'team.repair':
      // Check this team: the words the repair answered with, which name the member, project or machines.
      return failed || denied
        ? `A fix to the ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} was refused${str(d, 'words') ? `: ${str(d, 'words')}` : '.'}`
        : `The ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'name'))} was repaired${str(d, 'words') ? `: ${str(d, 'words')}` : '.'}`;
    case 'team.held':
      return `${str(d, 'hostname') ?? 'A machine'} now holds the ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'team'))}${str(d, 'scope') === 'local' ? ' (local to it)' : ''}.`;
    case 'team.held.version':
      return `${str(d, 'hostname') ?? 'A machine'} moved to version ${num(d, 'to') ?? '?'} of the team${quoted(str(d, 'team'))}.`;
    case 'team.dropped':
      return `${str(d, 'hostname') ?? 'A machine'} no longer holds the ${str(d, 'kind') === 'specialist' ? 'specialist' : 'team'}${quoted(str(d, 'team'))}.`;

    // Projects.
    case 'project.create':
      return `The project${quoted(str(d, 'name'))} was created.`;
    case 'project.update':
    case 'project.settings':
      return 'A project’s details were changed.';
    case 'project.rename':
      return `The project ${str(d, 'from') ? `“${str(d, 'from')}” ` : ''}was renamed${str(d, 'to') ? ` to “${str(d, 'to')}”` : ''}.`;
    case 'project.purpose':
      return d.set === false ? 'The project’s purpose was cleared; its roadmap’s line stands in again.' : 'The project’s purpose was written.';
    case 'project.classification':
      return str(d, 'root')
        ? `The repository${quoted(str(d, 'rootName') ?? str(d, 'root'))} was classified ${classWord(str(d, 'to'))}.`
        : `The project’s data was classified ${classWord(str(d, 'to'))}${str(d, 'from') ? ` (it was ${classWord(str(d, 'from'))})` : ''}.`;
    case 'assurance.override':
      return d && d.cleared
        ? `The organisation’s rating for ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'}) went back to Mojo Up’s.`
        : `The organisation rated ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'}) ${assuranceWord(str(d, 'to'))}${str(d, 'from') ? `, from ${assuranceWord(str(d, 'from'))}` : ''}.`;
    case 'assurance.assessment':
      return `The organisation assessed ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'}) as ${assuranceWord(str(d, 'to'))}${str(d, 'from') && str(d, 'from') !== str(d, 'to') ? `, from ${assuranceWord(str(d, 'from'))}` : ''}${str(d, 'reviewBy') ? `, to be reviewed by ${str(d, 'reviewBy')}` : ''}.`;
    case 'assurance.assessment.withdrawn':
      return `The organisation withdrew its assessment of ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'})${str(d, 'to') ? `; it is ${assuranceWord(str(d, 'to'))} again` : ''}.`;
    case 'assurance.assessment.review-due':
      return `The organisation’s assessment of ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'}) came due for review${str(d, 'reviewBy') ? ` on ${str(d, 'reviewBy')}` : ''}, and administrators were told.`;
    case 'assurance.evidence.upload':
      return `Evidence${quoted(a.target)} was uploaded for the organisation’s provider assessments.`;
    case 'assurance.provider.add':
      return d && d.restored ? `The organisation brought back its own provider${quoted(str(d, 'name'))}.` : `The organisation added its own provider${quoted(str(d, 'name'))} to Providers and Models.`;
    case 'assurance.provider.change':
      return `The organisation changed what its own provider ${str(d, 'provider')?.replace(/^org:/, '') ?? ''} is called or applies to.`;
    case 'assurance.provider.retire':
      return `The organisation retired its own provider${quoted(str(d, 'name'))}.`;
    case 'assurance.register.edit':
      return d && d.added
        ? `Mojo Up added ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'}) to the register it publishes, rated ${assuranceWord(str(d, 'to'))}.`
        : `Mojo Up ${d && d.restored ? 'brought back' : 'changed'} ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'}) in the register it publishes: ${assuranceWord(str(d, 'to'))}.`;
    case 'assurance.register.retire':
      return `Mojo Up retired ${str(d, 'provider') ?? 'a provider'} (${str(d, 'tier') ?? 'tier'}) from the register it publishes.`;
    case 'assurance.register.updated':
      return `Mojo Up’s assurance register was updated${num(d, 'to') !== undefined ? ` to revision ${num(d, 'to')}` : ''}${num(d, 'from') !== undefined ? ` (from revision ${num(d, 'from')})` : ''}; ratings the organisation has not overridden follow it.`;
    case 'project.archive':
      return `The project${quoted(str(d, 'name'))} was archived.`;
    case 'project.restore':
      return `The project${quoted(str(d, 'name'))} was brought back from the archive.`;
    case 'project.delete':
      return 'A project was deleted.';
    case 'project.area':
      return 'A project was moved to another business area.';
    case 'project.members':
      return `A project’s members changed${num(d, 'before') !== undefined && num(d, 'after') !== undefined ? ` from ${num(d, 'before')} to ${num(d, 'after')}` : ''}.`;
    case 'project.transfer':
      return 'A project was handed to a new owner.';
    case 'project.root':
      return 'A repository was added to a project.';
    case 'project.source':
      return 'A project’s link to its source host was cleared.';
    case 'project.map':
      return 'A project’s map of plans and roadmap was updated from a workbench.';
    case 'project.settings-layer':
      return `A project’s settings were changed${many(num(d, 'keys'), 'setting') ? ` (${many(num(d, 'keys'), 'setting')})` : ''}.`;
    case 'project.team.save':
      return `${str(d, 'name') ? `The team “${str(d, 'name')}”` : 'A team'} was saved in the project.`;
    case 'project.team.remove':
      return 'A team was removed from the project.';
    case 'project.roadmap':
      return `The project’s roadmap was updated${many(num(d, 'rows'), 'row') ? ` (${many(num(d, 'rows'), 'row')})` : ''}.`;
    case 'project.architecture':
      return 'The project’s architecture was updated.';
    case 'project.picture.save':
      return denied ? 'A picture could not be saved to the project.' : 'A picture was saved to the project.';
    case 'project.picture.remove':
      return denied ? 'A picture could not be removed from the project.' : 'A picture was removed from the project.';
    case 'project.risk.raise':
      return 'A risk was raised on the project.';
    case 'project.risk.change':
      return `A risk on the project was changed${str(d, 'status') ? ` (now ${plain(str(d, 'status'))})` : ''}.`;
    case 'project.risk.remove':
      return 'A risk was removed from the project.';
    case 'project.risk.settings':
      return 'The project’s risk settings were changed.';
    case 'project.join.request':
      return 'A person asked to join a project.';
    case 'project.join.approve':
      return 'A request to join a project was approved.';
    case 'project.join.decline':
      return `A request to join a project was declined${why}`;
    case 'project.merge.request':
      return 'A project asked to be merged into another.';
    case 'project.merge.decline':
      return `A request to merge a project was declined${why}`;
    case 'project.merged':
      return 'Projects were merged into one.';
    case 'project.promote.request':
      return 'A project was put forward to become an organisation project.';
    case 'project.promote.accept':
      return 'A project became an organisation project.';
    case 'project.promote.reject':
      return `A request to make a project an organisation project was rejected${why}`;
    case 'project.promote.withdraw':
      return 'A request to make a project an organisation project was withdrawn.';
    case 'work.claim':
      return `${str(d, 'ref') ? `“${str(d, 'ref')}”` : 'Some work'} was claimed.`;
    case 'work.takeover':
      return `${str(d, 'ref') ? `“${str(d, 'ref')}”` : 'Some work'} was taken over from its holder.`;
    case 'work.release':
      return `${str(d, 'ref') ? `“${str(d, 'ref')}”` : 'A claim on work'} was let go.`;
    case 'tracker.sync':
    case 'tracker.sync.requested':
      return `A tracker was synchronised${a.action === 'tracker.sync.requested' ? ' on request' : ''}${str(d, 'provider') ? ` with ${plain(str(d, 'provider'))}` : ''}.`;
    case 'tracker.conflict':
      return 'A change in the tracker clashed with the board; the board’s version was kept.';

    // Source hosts, repositories and pull requests.
    case 'source.connection.create':
      return `A connection to ${str(d, 'organisation') ?? plain(str(d, 'provider')) ?? 'a source host'} was added.`;
    case 'source.connection.update':
      return 'A connection to a source host was changed.';
    case 'source.connection.delete':
      return 'A connection to a source host was removed.';
    case 'source.credential':
      return `${str(d, 'repository') ? `The credential for ${str(d, 'repository')}` : 'A repository credential'} was refused: the machine does not act for that person.`;
    case 'source.identity.link':
      return `A person linked their ${plain(target) ?? 'source host'} account.`;
    case 'source.identity.unlink':
      return `A person unlinked their ${plain(target) ?? 'source host'} account.`;
    case 'pullRequest.create':
      return `A pull request${target ? ` (${target})` : ''} was opened${str(d, 'state') ? `, ${str(d, 'state')}` : ''}.`;
    case 'pullRequest.update':
      return `A pull request${target ? ` (${target})` : ''} changed${str(d, 'state') ? `, now ${str(d, 'state')}` : ''}.`;
    case 'pullRequest.act':
      return denied ? `A pull request${target ? ` (${target})` : ''} could not be ${str(d, 'action') ?? 'acted on'}${str(d, 'error') ? `: ${str(d, 'error')}` : ''}.` : `A pull request${target ? ` (${target})` : ''} was ${str(d, 'action') ?? 'acted on'}${str(d, 'as') ? `, as ${str(d, 'as')}` : ''}.`;

    // Microsoft Purview.
    case 'purview.connect':
      return 'Microsoft Purview was connected.';
    case 'purview.disconnect':
      return 'Microsoft Purview was disconnected.';
    case 'purview.settings':
      return 'The Microsoft Purview settings were changed.';
    case 'purview.try':
      return `A message was tried against Microsoft Purview${str(d, 'action') ? `: ${str(d, 'action')}` : ''}.`;
    case 'purview.activity.dismiss':
      return 'A Microsoft Purview problem was dismissed.';
    case 'purview.label.map':
      return `The Purview label${quoted(target)} was matched to a sensitivity.`;
    case 'purview.label.forget':
      return `The Purview label${quoted(target)} was forgotten.`;
    case 'purview.label.apply':
      return `A Purview label was asked for on ${target ?? 'a file'}.`;
    case 'purview.label.applied':
      return `A Purview label was applied to ${target ?? 'a file'}.`;
    case 'purview.label.apply-failed':
      return `A Purview label could not be applied to ${target ?? 'a file'}.`;
    case 'purview.location':
      return `A SharePoint place for files${target ? `, ${target}` : ''}, was set.`;

    // Releases and updates.
    case 'releases.approve':
      return `${target ?? 'A release'} was approved${str(d, 'ring') ? ` for the ${str(d, 'ring')} ring` : ''}.`;
    case 'releases.mirror':
      return `${target ?? 'A release'} was copied to the organisation’s own store.`;
    case 'releases.policy':
      return 'The update rings and rules were changed.';
    case 'releases.prune':
      return `${many(len(d, 'dropped'), 'old release') ?? 'Old releases'} ${len(d, 'dropped') === 1 ? 'was' : 'were'} cleared out.`;
    case 'releases.unpin':
      return `A machine was let update again${str(d, 'product') ? ` (${str(d, 'product')})` : ''}.`;
    case 'repository.setup': {
      // Set up for agents (workbench D272): the rows written, in words; never the files' content.
      const rows = Array.isArray(d.rows) ? (d.rows as string[]) : [];
      const word = (r: string) => (r === 'agents-md' ? 'AGENTS.md' : r === 'claude-md' ? 'CLAUDE.md' : r === 'copilot-instructions' ? 'Copilot instructions' : r === 'gitignore' ? 'the .gitignore guard' : r === 'memory' ? 'the memory' : r === 'optin' ? 'the opt-in' : r.startsWith('mcp:') ? `the ${r.slice(4)} tool server` : r.startsWith('hook:') ? `the ${r.slice(5)} hook` : r.startsWith('skill:') ? `the ${r.slice(6)} skill` : r);
      const where = target && target !== 'repository' ? target : 'a repository';
      if (denied) return `Setting up ${where} for agents was refused${a.error ? `: ${a.error}` : ''}.`;
      if (!rows.length) return `${where[0]!.toUpperCase()}${where.slice(1)} was checked for agents; nothing needed writing.`;
      return `${d.headless ? 'A node set up' : 'Set up'} ${where} for agents: wrote ${rows.length} ${rows.length === 1 ? 'thing' : 'things'} (${rows.slice(0, 6).map(word).join(', ')}${rows.length > 6 ? ', …' : ''}).`;
    }
    case 'policy.publish':
      return `${afterColon(target) ?? target ?? 'A policy'} was published${Array.isArray(d.sections) && d.sections.length ? ` (${(d.sections as string[]).join(', ')})` : ''}.`;
    case 'policy.remove':
      return `The policy ${target ?? ''} was removed.`.replace('  ', ' ');
    case 'remote-approvals.organisation': {
      const levels = Array.isArray(d.personDecides) ? (d.personDecides as string[]) : [];
      return levels.length ? `Each person now decides where they may approve ${levels.join(', ')}-risk requests on their own workbenches.` : 'The organisation now decides for everyone where requests may be approved.';
    }
    case 'remote-approvals.person': {
      const eff = (d.effective ?? {}) as Record<string, unknown>;
      const anywhere = ['low', 'medium', 'high'].filter((r) => eff[r] === 'allow');
      return `Someone changed where they approve requests on their own workbenches: ${anywhere.length ? `${anywhere.join(', ')} risk from anywhere` : 'only at the machine'}.`;
    }
    case 'settings-policy.update':
      return `The workbench settings policy was changed${many(num(d, 'keys'), 'setting') ? ` (${many(num(d, 'keys'), 'setting')})` : ''}.`;

    // Secrets and keys.
    case 'secret.create':
      return `${secretName(target)} was added.`;
    case 'secret.update':
      return `${secretName(target)} was changed.`;
    case 'secret.rotate':
      return `The key for ${afterColon(target) ?? 'a secret'} was rotated.`;
    case 'secret.remove':
      return `${secretName(target)} was removed.`;
    case 'secret.release':
      if (str(d, 'via') === 'foundry-order') return 'A node took the organisation’s Foundry sign-in to publish to Foundry for the person who asked.';
      return `${afterColon(target) ?? 'A secret'} was handed to a machine to use.`;
    case 'secret.stale':
      return `${secretName(target)} has not been rotated for ${num(d, 'rotateAfterDays') ?? 'too many'} days.`;
    case 'secret.node.allow':
      if (denied) return `Letting a node open ${plain(afterColon(target)) ? `${afterColon(target)}’s` : 'a person’s'} secrets was refused${refusalWords(str(d, 'refused'))}.`;
      return `A node was allowed to open ${plain(afterColon(target)) ? `${afterColon(target)}’s` : 'a person’s'} secrets${str(d, 'how') === 'consent' ? ', by their own consent' : ''}.`;
    case 'secret.node.withdraw':
      return denied ? `Stopping a node opening a person’s secrets was refused${refusalWords(str(d, 'refused'))}.` : 'A node may no longer open a person’s secrets.';
    case 'secret.policy':
      return 'The rules for secrets were changed.';
    case 'secret.policy.clear':
      return 'The rules for secrets were cleared.';
    case 'secret.sealingKey.register':
      return 'A machine registered a key for receiving secrets.';
    case 'secret.sealingKeys.list':
      return 'A node asked for the keys of other machines and was refused.';

    // Sentinel and subscriptions.
    case 'sentinel.connect':
      return 'Microsoft Sentinel was connected for audit export.';
    case 'sentinel.disconnect':
      return 'Microsoft Sentinel was disconnected.';
    case 'subscription.settings':
      return 'The settings for people’s own subscriptions were changed.';
    case 'subscription.sign-in.start':
      return `A person was asked to sign in to ${vendorText(str(d, 'vendor')) ?? 'a subscription'} on a machine.`;
    case 'subscription.sign-in.answer':
      return `A person answered a sign-in to ${plain(afterColon(target)) ?? 'a subscription'}.`;
    case 'subscription.sign-in':
      return d.ok === false || denied ? `A sign-in to ${afterColon(target) ?? 'a subscription'} did not work.` : `A sign-in to ${afterColon(target) ?? 'a subscription'} worked${str(d, 'plan') ? ` (${str(d, 'plan')} plan)` : ''}.`;
    case 'subscription.signed-in':
      return `A person is signed in to ${afterColon(target) ?? 'a subscription'} on a machine.`;
    case 'subscription.token.add':
      return `A person added their own ${afterColon(target) ?? 'subscription'} token${d.replaced === true ? ', replacing the old one' : ''}.`;
    case 'subscription.token.remove':
      return `A person removed their ${afterColon(target) ?? 'subscription'} token.`;
    case 'subscription.release':
      return `A person’s ${afterColon(target) ?? 'subscription'} sign-in was handed to a machine to use.`;
    case 'subscription.revoke':
      return `A person’s ${afterColon(target) ?? 'subscription'} sign-in was revoked${why}`;

    // Tools (tool servers).
    case 'tool.server.publish':
      return `${target ? `The tool server “${target}”` : 'A tool server'} was published.`;
    case 'tool.server.update':
      return `${target ? `The tool server “${target}”` : 'A tool server'} was changed.`;
    case 'tool.server.withdraw':
      return `${target ? `The tool server “${target}”` : 'A tool server'} was withdrawn.`;
    case 'tool.server.restore':
      return `${target ? `The tool server “${target}”` : 'A tool server'} was brought back.`;
    case 'tool.server.remove':
      return `${target ? `The tool server “${target}”` : 'A tool server'} was removed.`;
    case 'tool.review':
      return `${target ? `The tools of “${target}”` : 'Some tools'} were reviewed.`;
  }
  return undefined;
}


/** The payload, for a tooltip: readable, and never so long it fills the screen. */
export function payloadText(payload: unknown): string {
  const t = JSON.stringify(payload, null, 2) ?? '';
  return t.length > 1200 ? `${t.slice(0, 1200)}…` : t;
}
